import React, { useState, useEffect, useRef } from 'react';
import { Country, State } from 'country-state-city';
// Cities are 8 MB and only this form needs them — see utils/cityData.
import { City, useCitiesReady } from '../utils/cityData';
import { X, ExternalLink, Check, Mail, Save } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { dashboardAPI, leadsAPI } from '../services/api';
import FollowUpEmailsPanel from './FollowUpEmailsPanel';
import PhoneInput from './PhoneInput';
import { parseContactBook, formatContactBook, normalizeContactBook } from '../utils/contactBook';
import LeadMeetingsSection from './LeadMeetingsSection';
import { bdaOptions } from '../utils/bdeOwnership';
import { toViewableUrl } from '../utils/fileLinks';

// Using country-state-city for complete datasets


const LeadModal = ({ isOpen, onClose, onSubmit, lead, loading, onCloseDeal, onConvertToPotentialClient, inline = false, initialPrefill = null }) => {
  const { user } = useAuth();
  const [bdeUsers, setBdeUsers] = useState([]);
  const [visiblePocCount, setVisiblePocCount] = useState(1); // Track how many POC sections are visible
  const [postSalesData, setPostSalesData] = useState(null);
  // Inline edit-mode for the Post-Sales section. When true, render the
  // editable form instead of the read-only badges; draft lives in
  // postSalesForm, persisted via dashboardAPI.createOrUpdatePostSales.
  const [editingPostSales, setEditingPostSales] = useState(false);
  const [savingPostSales, setSavingPostSales] = useState(false);
  const [postSalesForm, setPostSalesForm] = useState({
    contact_number: '', time_zone: '', call_status: '', email_type: '', email: '',
    stage: 'followup', notes: '',
    follow_up_1_date: '', follow_up_1_note: ''
  });
  const [loadingPostSales, setLoadingPostSales] = useState(false);
  const [isInPotentialClients, setIsInPotentialClients] = useState(false);
  const [isInOnboardedClients, setIsInOnboardedClients] = useState(false);
  const [potentialClientId, setPotentialClientId] = useState(null);
  const [meetingSummaryFile, setMeetingSummaryFile] = useState(null);
  // Multi-file meeting summaries (lead-level). Array of { url, name, uploaded_at }.
  // The single `meetingSummaryFile` above is kept as the legacy/primary mirror
  // (and covers the potential-client fallback display).
  const [meetingSummaryFiles, setMeetingSummaryFiles] = useState([]);
  const [deletingFileUrl, setDeletingFileUrl] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [markingCompleted, setMarkingCompleted] = useState({}); // Track which follow-up is being marked
  const [followUpStatuses, setFollowUpStatuses] = useState({}); // Track completion status for each follow-up
  const [showEmailsPanel, setShowEmailsPanel] = useState(false);
  const [generatingAnalysis, setGeneratingAnalysis] = useState(false); // AI company-analysis generation in progress
  // Pre-populate BDE name with logged-in user's name (will be updated in useEffect when user is available)
  const [formData, setFormData] = useState({
    lead_date: new Date().toISOString().split('T')[0],
    company_name: '',
    company_url: '',
    ai_chat_url: '',
    linkedin_url: '',
    core_business: '',
    capacity_mw: '',
    bess_capacity_mw: '',
    residential_installations: '',
    ev_charging_assets: '',
    company_generic_number: '',
    city: '',
    state: '',
    country: '',
    location: '', // Keep for backward compatibility, will be auto-filled
    // POC 1 (Primary)
    poc_name: '',
    designation: '',
    email: '',
    contact_no: '',
    poc_linkedin_url: '',
    response_from_poc: '',
    poc_replied: '',
    // POC 2
    poc2_name: '',
    poc2_designation: '',
    poc2_email: '',
    poc2_contact_no: '',
    poc2_linkedin_url: '',
    poc2_response_from_poc: '',
    poc2_poc_replied: '',
    // POC 3
    poc3_name: '',
    poc3_designation: '',
    poc3_email: '',
    poc3_contact_no: '',
    poc3_linkedin_url: '',
    poc3_response_from_poc: '',
    poc3_poc_replied: '',
    // POC 4
    poc4_name: '',
    poc4_designation: '',
    poc4_email: '',
    poc4_contact_no: '',
    poc4_linkedin_url: '',
    poc4_response_from_poc: '',
    poc4_poc_replied: '',
    lead_source: '',
    custom_lead_source: '',
    stage: 'New',
    priority: 'Medium',
    interest_status: '',
    bde_name: user?.name || '',
    company_overview: '',
    contact_book: '',
    follow_up_date_1: '',
    follow_up_1_update: '',
    follow_up_date_2: '',
    follow_up_2_update: '',
    follow_up_date_3: '',
    follow_up_3_update: '',
    follow_up_status: 'Upcoming',
    // LinkedIn Activity Fields
    poc_shortlisted: 0,
    email_sent: 0,
    new_connection: 0,
    auto_reply_received: 0,
    first_sales_message_sent: '',
    created_at: '',
    updated_at: ''
  });

  const [availableCountries, setAvailableCountries] = useState([]);
  const [availableStates, setAvailableStates] = useState([]);
  const [availableCities, setAvailableCities] = useState([]);
  // The city list arrives on its own schedule (8 MB, fetched when this form
  // opens rather than when the app starts). Re-run the two effects below the
  // moment it lands so the dropdowns fill themselves.
  const citiesReady = useCitiesReady();
  const [selectedCountryCode, setSelectedCountryCode] = useState('');
  const [selectedStateCode, setSelectedStateCode] = useState('');
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [cityDropdownOpen, setCityDropdownOpen] = useState(false);
  const [countrySearchQuery, setCountrySearchQuery] = useState('');
  const [citySearchQuery, setCitySearchQuery] = useState('');
  const countryInputRef = useRef(null);
  const cityInputRef = useRef(null);
  const stateInputRef = useRef(null);
  const formRef = useRef(null);
  // Synchronous double-submit guard — see handleSubmit. Reset by parent's
  // `loading` prop transition or by the 3s setTimeout fallback.
  const submittingRef = useRef(false);
  // The lead list no longer carries the three heavy text columns (contact_book,
  // company_overview, ai_chat_url) — they arrive with the getById below. Until
  // they do, this form does not know them, and must not save over them.
  const hydratedRef = useRef(false);
  // Track which lead-id we last hydrated from so the lead-prop effect only
  // resets state when the user opens a *different* lead (not on every parent
  // refetch that recreates the object identity).
  const hydratedLeadIdRef = useRef(null);
  const countrySearchRef = useRef('');
  const stateSearchRef = useRef('');
  const citySearchRef = useRef('');
  const [stateDropdownOpen, setStateDropdownOpen] = useState(false);
  const [stateSearchQuery, setStateSearchQuery] = useState('');
  const [countryHighlight, setCountryHighlight] = useState(0);
  const [stateHighlight, setStateHighlight] = useState(0);
  const [cityHighlight, setCityHighlight] = useState(0);
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [addContactName, setAddContactName] = useState('');
  const [addContactEmail, setAddContactEmail] = useState('');

  const capitalizeFirst = (text) => {
    if (!text) return text;
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  // Focus inputs when dropdowns open
  useEffect(() => {
    if (countryDropdownOpen && countryInputRef.current) {
      countryInputRef.current.focus();
    }
  }, [countryDropdownOpen]);

  useEffect(() => {
    if (cityDropdownOpen && cityInputRef.current) {
      cityInputRef.current.focus();
    }
  }, [cityDropdownOpen]);

  useEffect(() => {
    if (stateDropdownOpen && stateInputRef.current) {
      stateInputRef.current.focus();
    }
  }, [stateDropdownOpen]);

  useEffect(() => { setCountryHighlight(0); }, [countrySearchQuery, countryDropdownOpen]);
  useEffect(() => { setStateHighlight(0); }, [stateSearchQuery, stateDropdownOpen]);
  useEffect(() => { setCityHighlight(0); }, [citySearchQuery, cityDropdownOpen]);

  const navigateList = (e, count, highlight, setHighlight, onPick, onClose) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => (count === 0 ? 0 : (h + 1) % count));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => (count === 0 ? 0 : (h - 1 + count) % count));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (count > 0) onPick(highlight);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  // Load all countries once
  useEffect(() => {
    setAvailableCountries(Country.getAllCountries());
  }, []);

  // Load BDE users from API
  useEffect(() => {
    const fetchBDEUsers = async () => {
      try {
        const response = await dashboardAPI.getBDEUsers();
        if (response && response.data) {
          setBdeUsers(response.data);
        }
      } catch (error) {
        console.error('Error fetching BDE users:', error);
      }
    };
    if (isOpen) {
      fetchBDEUsers();
    }
  }, [isOpen]);

  // Fetch post sales data when editing a lead
  useEffect(() => {
    const fetchPostSales = async () => {
      if (lead && lead.id) {
        try {
          setLoadingPostSales(true);
          const response = await dashboardAPI.getPostSales({ lead_id: lead.id });
          if (response && response.success && response.data && response.data.length > 0) {
            setPostSalesData(response.data[0]);
          } else {
            setPostSalesData(null);
          }
        } catch (error) {
          console.error('Error fetching post sales:', error);
          setPostSalesData(null);
        } finally {
          setLoadingPostSales(false);
        }
      } else {
        setPostSalesData(null);
      }
    };
    
    if (isOpen && lead) {
      fetchPostSales();
    }
  }, [isOpen, lead]);

  // Hydrate the inline edit form from the loaded post-sales record. Keeps the
  // draft in sync when re-opening the modal on a different lead.
  useEffect(() => {
    setPostSalesForm({
      contact_number: postSalesData?.contact_number || '',
      time_zone: postSalesData?.time_zone || '',
      call_status: postSalesData?.call_status || '',
      email_type: postSalesData?.email_type || '',
      email: postSalesData?.email || '',
      stage: postSalesData?.stage || 'followup',
      notes: postSalesData?.notes || '',
      follow_up_1_date: postSalesData?.follow_up_1_date
        ? String(postSalesData.follow_up_1_date).slice(0, 10) : '',
      follow_up_1_note: postSalesData?.follow_up_1_note || ''
    });
    setEditingPostSales(false);
  }, [postSalesData]);

  // Save the inline post-sales edits. Sends only the fields the inline form
  // exposes so we don't accidentally clobber follow-ups 2-4 or the
  // post_sales_bde assignment owned by the Post-Sales tab.
  const handleSavePostSales = async () => {
    if (!lead?.id) return;
    setSavingPostSales(true);
    try {
      const payload = {
        lead_id: lead.id,
        contact_number: postSalesForm.contact_number || null,
        time_zone: postSalesForm.time_zone || null,
        call_status: postSalesForm.call_status || null,
        email_type: postSalesForm.email_type || null,
        email: postSalesForm.email || null,
        stage: postSalesForm.stage || 'followup',
        notes: postSalesForm.notes || null,
        // Only first follow-up surfaced inline. Others (2-4) stay editable in
        // the dedicated Post-Sales tab.
        follow_ups: [{
          date: postSalesForm.follow_up_1_date || null,
          note: postSalesForm.follow_up_1_note || ''
        }],
        // Preserve existing assignment to avoid re-assigning when an admin opens.
        post_sales_bde_id: postSalesData?.post_sales_bde_id ?? (user?.id || null),
        post_sales_bde_name: postSalesData?.post_sales_bde_name ?? (user?.name || null)
      };
      const resp = await dashboardAPI.createOrUpdatePostSales(payload);
      if (resp?.success) {
        // Refresh the record so the read-only view reflects the saved values.
        const refresh = await dashboardAPI.getPostSales({ lead_id: lead.id });
        if (refresh?.success && refresh.data?.length) setPostSalesData(refresh.data[0]);
        setEditingPostSales(false);
        alert('Post-Sales info saved.');
      } else {
        alert('Save failed: ' + (resp?.error || 'Unknown error'));
      }
    } catch (e) {
      alert('Save failed: ' + (e.response?.data?.error || e.message));
    } finally {
      setSavingPostSales(false);
    }
  };

  // When country changes, load states and reset state/city (but preserve if editing)
  useEffect(() => {
    if (selectedCountryCode) {
      const states = State.getStatesOfCountry(selectedCountryCode) || [];
      setAvailableStates(states);
      
      // If country has no states (or very few), load cities directly for the country
      if (states.length === 0) {
        const allCities = City.getCitiesOfCountry(selectedCountryCode) || [];
        setAvailableCities([...new Set(allCities.map(c => c.name))]);
      } else {
        // If states exist, clear cities until a state is selected
        if (!lead) {
          setAvailableCities([]);
        }
      }
    } else {
      setAvailableStates([]);
      if (!lead) {
        setAvailableCities([]);
      }
    }
    // Only reset state/city if we're not editing (i.e., no lead prop)
    if (!lead) {
      setSelectedStateCode('');
      setFormData(prev => ({ ...prev, city: '', state: '' }));
    }
  }, [selectedCountryCode, lead, citiesReady]);

  // When state changes, load cities (but preserve city if editing and it exists)
  useEffect(() => {
    if (selectedCountryCode && selectedStateCode) {
      const cities = City.getCitiesOfState(selectedCountryCode, selectedStateCode) || [];
      setAvailableCities(cities.map(c => c.name));
      // Only clear city if it doesn't exist in the new list AND we're not editing
      if (!lead && formData.city && !cities.find(c => c.name === formData.city)) {
        setFormData(prev => ({ ...prev, city: '' }));
      }
    } else if (selectedCountryCode && !selectedStateCode) {
      // If country selected but no state, try to load all cities for country (for countries without states)
      const allCities = City.getCitiesOfCountry(selectedCountryCode) || [];
      if (allCities.length > 0) {
        setAvailableCities([...new Set(allCities.map(c => c.name))]);
      } else {
        // Only clear cities if we're not editing
        if (!lead) {
          setAvailableCities([]);
        }
      }
    } else if (!selectedCountryCode) {
      // Only clear cities if no country selected AND we're not editing
      if (!lead) {
        setAvailableCities([]);
      }
    }
  }, [selectedCountryCode, selectedStateCode, lead, formData.city, citiesReady]);

  // Ctrl+S / Cmd+S to save lead form when modal is open
  useEffect(() => {
    if (!isOpen) {
      // Reset so reopening forces a fresh hydration from the latest lead data.
      hydratedLeadIdRef.current = null;
      return;
    }
    const onAppSave = () => formRef.current?.requestSubmit();
    window.addEventListener('app-save', onAppSave);
    return () => window.removeEventListener('app-save', onAppSave);
  }, [isOpen]);

  useEffect(() => {
    // Helper function to ensure date is in correct format without timezone issues
    const formatDate = (dateString) => {
      if (!dateString) return '';
      // If it's already in YYYY-MM-DD format, return as is
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dateString;
      }
      // Otherwise, convert to YYYY-MM-DD without timezone conversion
      const date = new Date(dateString + 'T00:00:00');
      return date.toISOString().split('T')[0];
    };

    console.log('LeadModal: lead prop changed:', lead);
    if (lead) {
      // Don't reset when editing - we'll populate from lead data
      console.log('LeadModal: LinkedIn Activity data:', {
        poc_shortlisted: lead.poc_shortlisted,
        email_sent: lead.email_sent,
        new_connection: lead.new_connection,
        auto_reply_received: lead.auto_reply_received
      });
      console.log('LeadModal: Follow-up data:', {
        follow_up_date_1: lead.follow_up_date_1,
        follow_up_1_update: lead.follow_up_1_update,
        follow_up_status: lead.follow_up_status,
        follow_up_status_1: lead.follow_up_status,
        follow_up_status_2: lead.follow_up_status_2,
        follow_up_status_3: lead.follow_up_status_3
      });
      
      // Initialize follow-up completion statuses only when opening a *new*
      // lead. A parent refetch (e.g. after clicking Done) recreates the lead
      // object with the same id and re-runs this effect; without this guard
      // we'd clobber the user's just-toggled state with list-view data that
      // can be momentarily stale, causing the Done button to flicker
      // grey→green→grey.
      if (hydratedLeadIdRef.current !== lead.id) {
        hydratedLeadIdRef.current = lead.id;
        const fu1InitialStatus = (lead.follow_up_status_1 || lead.follow_up_status || '').toLowerCase();
        setFollowUpStatuses({
          1: fu1InitialStatus === 'completed',
          2: lead.follow_up_status_2?.toLowerCase() === 'completed',
          3: lead.follow_up_status_3?.toLowerCase() === 'completed'
        });
      }
      // Handle lead source logic - check if it's a custom name
      const predefinedSources = ['LinkedIn', 'Email', 'Referral', 'Website', 'Cold Call', 'Event'];
      
      // Map old "Trade Show" to "Event" for backward compatibility
      let normalizedLeadSource = lead.lead_source;
      if (normalizedLeadSource === 'Trade Show') {
        normalizedLeadSource = 'Event';
      }
      
      const isCustomSource = normalizedLeadSource && !predefinedSources.includes(normalizedLeadSource);
      
      // When editing an existing lead, populate form with lead data
      // First, set up country/state codes for dropdowns
      // Wait for countries to load if they're not ready yet
      if (lead.country) {
        if (availableCountries.length > 0) {
          const countryMatch = availableCountries.find(c => c.name === lead.country);
          if (countryMatch) {
            const isoCode = countryMatch.isoCode;
            setSelectedCountryCode(isoCode);
            // Load states for this country
            const states = State.getStatesOfCountry(isoCode) || [];
            setAvailableStates(states);
            
            // If state is provided, set it up (extract from location if needed)
            let stateToMatch = lead.state || '';
            if (!stateToMatch && lead.location) {
              const locationParts = lead.location.split(',').map(s => s.trim());
              if (locationParts.length === 3) {
                stateToMatch = locationParts[1];
              }
            }
            
            if (stateToMatch && states.length > 0) {
              // Try exact match first
              let stateMatch = states.find(s => s.name === stateToMatch);
              
              // If not found, try case-insensitive match
              if (!stateMatch) {
                stateMatch = states.find(s => s.name.toLowerCase() === stateToMatch.toLowerCase());
              }
              
              // If still not found, try partial match
              if (!stateMatch) {
                stateMatch = states.find(s => s.name.toLowerCase().includes(stateToMatch.toLowerCase()) || stateToMatch.toLowerCase().includes(s.name.toLowerCase()));
              }
              
              if (stateMatch) {
                const stateIsoCode = stateMatch.isoCode;
                setSelectedStateCode(stateIsoCode);
                // Load cities for this state
                const cities = City.getCitiesOfState(isoCode, stateIsoCode) || [];
                setAvailableCities(cities.map(c => c.name));
                // Update formData state to match the found state name (use setTimeout to avoid race condition)
                setTimeout(() => {
                  setFormData(prev => ({ ...prev, state: stateMatch.name }));
                }, 0);
              }
            } else {
              // If no state found but country is selected, try to load cities for the country
              // This handles countries without states (like UK, some others)
              const allCities = City.getCitiesOfCountry(isoCode) || [];
              if (allCities.length > 0) {
                setAvailableCities([...new Set(allCities.map(c => c.name))]);
              }
              
              // Also try to extract state from location if it exists
              if (lead.location) {
                const locationParts = lead.location.split(',').map(s => s.trim());
                if (locationParts.length === 3) {
                  const stateToExtract = locationParts[1];
                  // Try exact match first, then case-insensitive, then partial
                  let stateMatch = states.find(s => s.name === stateToExtract);
                  if (!stateMatch) {
                    stateMatch = states.find(s => s.name.toLowerCase() === stateToExtract.toLowerCase());
                  }
                  if (!stateMatch) {
                    stateMatch = states.find(s => s.name.toLowerCase().includes(stateToExtract.toLowerCase()) || stateToExtract.toLowerCase().includes(s.name.toLowerCase()));
                  }
                  
                  if (stateMatch) {
                    const stateIsoCode = stateMatch.isoCode;
                    setSelectedStateCode(stateIsoCode);
                    const cities = City.getCitiesOfState(isoCode, stateIsoCode) || [];
                    setAvailableCities(cities.map(c => c.name));
                    setTimeout(() => {
                      setFormData(prev => ({ ...prev, state: stateMatch.name }));
                    }, 0);
                  }
                }
              }
            }
          }
        } else {
          // Countries not loaded yet, set up after a short delay
          setTimeout(() => {
            const countryMatch = availableCountries.find(c => c.name === lead.country);
            if (countryMatch) {
              const isoCode = countryMatch.isoCode;
              setSelectedCountryCode(isoCode);
              const states = State.getStatesOfCountry(isoCode) || [];
              setAvailableStates(states);
              
              // Extract state from location if not in lead.state
              let stateToMatch = lead.state || '';
              if (!stateToMatch && lead.location) {
                const locationParts = lead.location.split(',').map(s => s.trim());
                if (locationParts.length === 3) {
                  stateToMatch = locationParts[1];
                }
              }
              
              if (stateToMatch && states.length > 0) {
                // Try exact match first
                let stateMatch = states.find(s => s.name === stateToMatch);
                
                // If not found, try case-insensitive match
                if (!stateMatch) {
                  stateMatch = states.find(s => s.name.toLowerCase() === stateToMatch.toLowerCase());
                }
                
                // If still not found, try partial match
                if (!stateMatch) {
                  stateMatch = states.find(s => s.name.toLowerCase().includes(stateToMatch.toLowerCase()) || stateToMatch.toLowerCase().includes(s.name.toLowerCase()));
                }
                
                if (stateMatch) {
                  const stateIsoCode = stateMatch.isoCode;
                  setSelectedStateCode(stateIsoCode);
                  const cities = City.getCitiesOfState(isoCode, stateIsoCode) || [];
                  setAvailableCities(cities.map(c => c.name));
                }
              }
            }
          }, 100);
        }
      }
      
      // Parse location field to extract state and city if not already in lead.state/city
      let parsedState = lead.state || '';
      let parsedCity = lead.city || '';
      if (lead.location && (!parsedCity || !parsedState)) {
        // Location format: "City, State, Country" or "City, Country"
        const locationParts = lead.location.split(',').map(s => s.trim());
        if (locationParts.length === 3) {
          // Format: "City, State, Country"
          if (!parsedCity) parsedCity = locationParts[0];
          if (!parsedState) parsedState = locationParts[1];
        } else if (locationParts.length === 2) {
          // Format: "City, Country"
          if (!parsedCity) parsedCity = locationParts[0];
        }
      }
      
      // Fill the form from what the list ALREADY has, then merge in what only
      // the server knows when it arrives.
      //
      // This used to be one async block that awaited getById before it set a
      // single field, so opening a company showed a form of blank sections for
      // as long as that round trip took — every section, every time, for data
      // the row on screen was already holding.
      setTimeout(() => {
      const normalizeUrl = (url) => {
        if (!url) return '';
        return url.charAt(0).toLowerCase() + url.slice(1);
      };
      const applyLead = (sourceLead) => {
        setFormData({
        lead_date: formatDate(sourceLead.lead_date) || new Date().toISOString().split('T')[0],
        company_name: sourceLead.company_name || '',
        company_url: normalizeUrl(sourceLead.company_url || ''),
        ai_chat_url: normalizeUrl(sourceLead.ai_chat_url || ''),
        linkedin_url: normalizeUrl(sourceLead.linkedin_url || ''),
        core_business: sourceLead.core_business || '',
        company_overview: sourceLead.company_overview || '',
        contact_book: (sourceLead.contact_book != null && sourceLead.contact_book !== '') ? normalizeContactBook(String(sourceLead.contact_book).trim()) : (sourceLead.contact_book || ''),
          capacity_mw: sourceLead.capacity_mw || '',
          bess_capacity_mw: sourceLead.bess_capacity_mw || '',
          residential_installations: (sourceLead.residential_installations == null ? '' : sourceLead.residential_installations),
          ev_charging_assets: (sourceLead.ev_charging_assets == null ? '' : sourceLead.ev_charging_assets),
          company_generic_number: sourceLead.company_generic_number || '',
          city: parsedCity,
          state: parsedState,
        country: sourceLead.country || '',
        location: sourceLead.location || '',
        poc_name: sourceLead.poc_name || '',
        designation: sourceLead.designation || '',
        email: sourceLead.email || '',
        contact_no: sourceLead.contact_no || '',
        poc_linkedin_url: normalizeUrl(sourceLead.poc_linkedin_url || ''),
        response_from_poc: sourceLead.response_from_poc || '',
        poc_replied: (sourceLead.poc_replied !== null && sourceLead.poc_replied !== undefined) ? String(sourceLead.poc_replied) : '',
        poc2_name: sourceLead.poc2_name || '',
        poc2_designation: sourceLead.poc2_designation || '',
        poc2_email: sourceLead.poc2_email || '',
        poc2_contact_no: sourceLead.poc2_contact_no || '',
        poc2_linkedin_url: normalizeUrl(sourceLead.poc2_linkedin_url || ''),
        poc2_response_from_poc: sourceLead.poc2_response_from_poc || '',
        poc2_poc_replied: (sourceLead.poc2_poc_replied !== null && sourceLead.poc2_poc_replied !== undefined) ? String(sourceLead.poc2_poc_replied) : '',
        poc3_name: sourceLead.poc3_name || '',
        poc3_designation: sourceLead.poc3_designation || '',
        poc3_email: sourceLead.poc3_email || '',
        poc3_contact_no: sourceLead.poc3_contact_no || '',
        poc3_linkedin_url: normalizeUrl(sourceLead.poc3_linkedin_url || ''),
        poc3_response_from_poc: sourceLead.poc3_response_from_poc || '',
        poc3_poc_replied: (sourceLead.poc3_poc_replied !== null && sourceLead.poc3_poc_replied !== undefined) ? String(sourceLead.poc3_poc_replied) : '',
        poc4_name: sourceLead.poc4_name || '',
        poc4_designation: sourceLead.poc4_designation || '',
        poc4_email: sourceLead.poc4_email || '',
        poc4_contact_no: sourceLead.poc4_contact_no || '',
        poc4_linkedin_url: normalizeUrl(sourceLead.poc4_linkedin_url || ''),
        poc4_response_from_poc: sourceLead.poc4_response_from_poc || '',
        poc4_poc_replied: (sourceLead.poc4_poc_replied !== null && sourceLead.poc4_poc_replied !== undefined) ? String(sourceLead.poc4_poc_replied) : '',
        lead_source: isCustomSource ? 'Other' : (normalizedLeadSource || ''),
        custom_lead_source: isCustomSource ? normalizedLeadSource : '',
        stage: sourceLead.stage || 'New',
        priority: sourceLead.priority || 'Medium',
        interest_status: sourceLead.interest_status || '',
        bde_name: sourceLead.bde_name || '',
        follow_up_date_1: formatDate(sourceLead.follow_up_date_1),
        follow_up_1_update: sourceLead.follow_up_1_update || '',
        follow_up_date_2: formatDate(sourceLead.follow_up_date_2),
        follow_up_2_update: sourceLead.follow_up_2_update || '',
        follow_up_date_3: formatDate(sourceLead.follow_up_date_3),
        follow_up_3_update: sourceLead.follow_up_3_update || '',
        follow_up_status: sourceLead.follow_up_status || 'Upcoming',
        follow_up_status_1: sourceLead.follow_up_status_1 || sourceLead.follow_up_status || '',
        follow_up_status_2: sourceLead.follow_up_status_2 || '',
        follow_up_status_3: sourceLead.follow_up_status_3 || '',
        poc_shortlisted: sourceLead.poc_shortlisted || 0,
        email_sent: sourceLead.email_sent || 0,
        new_connection: sourceLead.new_connection || 0,
        auto_reply_received: sourceLead.auto_reply_received || 0,
        first_sales_message_sent: formatDate(sourceLead.first_sales_message_sent),
        created_at: sourceLead.created_at || '',
        updated_at: sourceLead.updated_at || ''
      });
      // Re-sync the Done-button state from the fully-hydrated lead. The
      // initial sync from the list-view `lead` prop above can be stale
      // (e.g. after reopening the modal before the parent refetch finishes),
      // so overwrite with the authoritative per-row statuses from getById.
      setFollowUpStatuses({
        1: (sourceLead.follow_up_status_1 || '').toLowerCase() === 'completed',
        2: (sourceLead.follow_up_status_2 || '').toLowerCase() === 'completed',
        3: (sourceLead.follow_up_status_3 || '').toLowerCase() === 'completed'
      });

      let pocCount = 1;
      if (sourceLead.poc2_name || sourceLead.poc2_email || sourceLead.poc2_contact_no || sourceLead.poc2_poc_replied || sourceLead.poc2_response_from_poc || sourceLead.poc2_designation) pocCount = 2;
      if (sourceLead.poc3_name || sourceLead.poc3_email || sourceLead.poc3_contact_no || sourceLead.poc3_poc_replied || sourceLead.poc3_response_from_poc || sourceLead.poc3_designation) pocCount = 3;
      if (sourceLead.poc4_name || sourceLead.poc4_email || sourceLead.poc4_contact_no || sourceLead.poc4_poc_replied || sourceLead.poc4_response_from_poc || sourceLead.poc4_designation) pocCount = 4;
      setVisiblePocCount(pocCount);

      // Hydrate the lead-level meeting summary file(s). Set authoritatively
      // (including empty) so switching between leads never leaves a stale file
      // on screen. The multi-file array is the source of truth; the single
      // `meetingSummaryFile` mirrors the first entry (and the PC-upgrade path
      // below can still point it at a potential-client file when one exists).
      const hydratedFiles = Array.isArray(sourceLead.meeting_summary_files)
        ? sourceLead.meeting_summary_files.filter(f => f && f.url)
        : (sourceLead.meeting_summary_file ? [{ url: sourceLead.meeting_summary_file, name: null }] : []);
      setMeetingSummaryFiles(hydratedFiles);
      setMeetingSummaryFile(hydratedFiles[0]?.url || sourceLead.meeting_summary_file || null);
      };

      hydratedRef.current = false;
      applyLead({ ...lead });
      (async () => {
        try {
          const res = await leadsAPI.getById(lead.id);
          const full = res?.data ?? res;
          if (!full) return;
          hydratedRef.current = true;
          // The list view can be stale for columns added after
          // sales_leads_view was created (ai_chat_url, contact_book);
          // getLeadById reads those straight from `leads`.
          applyLead({
            ...lead,
            ...full,
            contact_book: full.contact_book != null ? String(full.contact_book).trim() : (lead.contact_book ?? ''),
            ai_chat_url: full.ai_chat_url != null ? String(full.ai_chat_url) : (lead.ai_chat_url ?? '')
          });
        } catch (_) {}
      })();
      }, 50);
    } else {
      // When creating a new lead, reset to default values
      setVisiblePocCount(1); // Start with just POC 1
      // Also reset dropdown states and search queries
      setSelectedCountryCode('');
      setSelectedStateCode('');
      setAvailableStates([]);
      setAvailableCities([]);
      setCountrySearchQuery('');
      setStateSearchQuery('');
      setCitySearchQuery('');
      setCountryDropdownOpen(false);
      setStateDropdownOpen(false);
      setCityDropdownOpen(false);
      
      // Pre-populate BDE name with logged-in user's name
      // Find the matching BDE user name from the database
      const loggedInBdeName = user?.name || '';
      // Try to find exact match in bdeUsers, otherwise use the user's name as-is
      const matchingBde = bdeUsers.find(bde => 
        bde.name?.toLowerCase() === loggedInBdeName.toLowerCase() ||
        bde.name?.includes(loggedInBdeName) ||
        loggedInBdeName.includes(bde.name)
      );
      const defaultBdeName = matchingBde?.name || loggedInBdeName;
      const prefill = initialPrefill || {};
      setFormData({
        lead_date: new Date().toISOString().split('T')[0],
        company_name: prefill.company_name || '',
        company_url: prefill.company_url || '',
        ai_chat_url: prefill.ai_chat_url || '',
        linkedin_url: prefill.linkedin_url || '',
        core_business: prefill.core_business || '',
        capacity_mw: '',
        bess_capacity_mw: '',
        residential_installations: '',
        ev_charging_assets: '',
        company_generic_number: '',
        city: prefill.city || '',
        state: prefill.state || '',
        country: prefill.country || '',
        location: prefill.location || '',
        poc_name: '',
        designation: '',
        email: '',
        contact_no: '',
        poc_linkedin_url: '',
        response_from_poc: '',
        poc_replied: '',
        poc2_name: '',
        poc2_designation: '',
        poc2_email: '',
        poc2_contact_no: '',
        poc2_linkedin_url: '',
        poc2_response_from_poc: '',
        poc2_poc_replied: '',
        poc3_name: '',
        poc3_designation: '',
        poc3_email: '',
        poc3_contact_no: '',
        poc3_linkedin_url: '',
        poc3_response_from_poc: '',
        poc3_poc_replied: '',
        poc4_name: '',
        poc4_designation: '',
        poc4_email: '',
        poc4_contact_no: '',
        poc4_linkedin_url: '',
        poc4_response_from_poc: '',
        poc4_poc_replied: '',
        lead_source: '',
        custom_lead_source: '',
        stage: 'New',
        priority: 'Medium',
        bde_name: defaultBdeName,
        company_overview: prefill.company_overview || '',
        contact_book: '',
        follow_up_date_1: '',
        follow_up_1_update: '',
        follow_up_date_2: '',
        follow_up_2_update: '',
        follow_up_date_3: '',
        follow_up_3_update: '',
        follow_up_status: 'Upcoming',
        // LinkedIn Activity Fields
        poc_shortlisted: 0,
        email_sent: 0,
        new_connection: 0,
        auto_reply_received: 0,
        first_sales_message_sent: '',
        created_at: '',
        updated_at: ''
      });
    }
  }, [lead, availableCountries, isOpen, user, bdeUsers, initialPrefill]);
  
  // Update BDE name when user changes and form is for a new lead
  useEffect(() => {
    if (!lead && user?.name && isOpen && bdeUsers.length > 0) {
      // Find the matching BDE user name from the database
      const loggedInBdeName = user?.name || '';
      const matchingBde = bdeUsers.find(bde => 
        bde.name?.toLowerCase() === loggedInBdeName.toLowerCase() ||
        bde.name?.includes(loggedInBdeName) ||
        loggedInBdeName.includes(bde.name)
      );
      const defaultBdeName = matchingBde?.name || loggedInBdeName;
      
      // Only update if it's a new lead and BDE name is empty or doesn't match
      setFormData(prev => {
        if (!prev.bde_name || prev.bde_name !== defaultBdeName) {
          return { ...prev, bde_name: defaultBdeName };
        }
        return prev;
      });
    }
  }, [user, isOpen, lead, bdeUsers]);
  
  // Check if lead is already in potential clients
  useEffect(() => {
    const checkPotentialClient = async () => {
      if (lead && lead.id && isOpen) {
        try {
          console.log('🔍 Checking if lead is in potential clients:', lead.id, lead.company_name);
          
          // Always check fresh from database when modal opens
          const result = await dashboardAPI.checkLeadInPotentialClients(lead.id);
          console.log('✅ Check result:', result);
          console.log('✅ result.success:', result.success);
          console.log('✅ result.isInPotentialClients:', result.isInPotentialClients);
          console.log('✅ result.data:', result.data);
          
          // Check both the flag and if data exists (data.id means it exists)
          const isInPC = result.success && (result.isInPotentialClients === true || (result.data && result.data.id));
          const isInOC = result.success && result.isInOnboardedClients === true;
          console.log('📊 Setting isInPotentialClients to:', isInPC);
          console.log('📊 Setting isInOnboardedClients to:', isInOC);
          
          // Always update state immediately
          setIsInPotentialClients(isInPC);
          setIsInOnboardedClients(isInOC);
          console.log('📊 isInPotentialClients state updated to:', isInPC);
          console.log('📊 isInOnboardedClients state updated to:', isInOC);
          
          if (result.success && result.data && result.data.id) {
            setPotentialClientId(result.data.id);
            // Prefer the potential-client file when it has one, but never wipe
            // the lead-level file (hydrated above) just because the PC row has none.
            setMeetingSummaryFile(prev => result.data.meeting_summary_file || prev || null);
            console.log('📁 Found potential client ID:', result.data.id);
          } else if (!isInPC) {
            // Not a potential client — clear only the PC id. The lead-level
            // meeting-summary file stays (it lives on the lead, not the PC row).
            setPotentialClientId(null);
          }
        } catch (error) {
          console.error('❌ Error checking potential client:', error);
          console.error('❌ Error details:', error.response?.data || error.message);
          
          // On error, retry multiple times with increasing delays
          const retryDelays = [500, 1000, 2000];
          retryDelays.forEach((delay, index) => {
            setTimeout(async () => {
              try {
                console.log(`🔄 Retry ${index + 1} checking for lead:`, lead.id);
                const retryResult = await dashboardAPI.checkLeadInPotentialClients(lead.id);
                console.log(`🔄 Retry ${index + 1} result:`, retryResult);
                if (retryResult.success) {
                  const isInPC = retryResult.isInPotentialClients === true || (retryResult.data && retryResult.data.id);
                  const isInOC = retryResult.isInOnboardedClients === true;
                  console.log(`🔄 Retry ${index + 1} - Setting isInPotentialClients to:`, isInPC);
                  console.log(`🔄 Retry ${index + 1} - Setting isInOnboardedClients to:`, isInOC);
                  setIsInPotentialClients(isInPC);
                  setIsInOnboardedClients(isInOC);
                  if (retryResult.data && retryResult.data.id) {
                    setPotentialClientId(retryResult.data.id);
                    setMeetingSummaryFile(prev => retryResult.data.meeting_summary_file || prev || null);
                  }
                }
              } catch (retryError) {
                console.error(`❌ Retry ${index + 1} also failed:`, retryError);
                // On last retry, if still failing, assume not in potential clients
                if (index === retryDelays.length - 1) {
                  setIsInPotentialClients(false);
                }
              }
            }, delay);
          });
        }
      } else if (!lead || !lead.id) {
        // Only reset if there's no lead
        console.log('🔄 No lead or lead.id, resetting isInPotentialClients and isInOnboardedClients to false');
        setIsInPotentialClients(false);
        setIsInOnboardedClients(false);
        setPotentialClientId(null);
        setMeetingSummaryFile(null);
        setMeetingSummaryFiles([]);
      }
    };
    
    // Check immediately when modal opens or lead changes
    if (isOpen && lead?.id) {
      console.log('🚀 Starting potential client check for lead:', lead.id);
      checkPotentialClient();
      // Also check after a small delay to ensure database is updated
      const timeoutId = setTimeout(() => {
        console.log('⏰ Delayed check (500ms) for lead:', lead.id);
        checkPotentialClient();
      }, 500);
      
      // Check again after a longer delay to catch any race conditions
      const timeoutId2 = setTimeout(() => {
        console.log('⏰ Delayed check (1500ms) for lead:', lead.id);
        checkPotentialClient();
      }, 1500);
      
      // Check one more time after 3 seconds to be absolutely sure
      const timeoutId3 = setTimeout(() => {
        console.log('⏰ Final check (3000ms) for lead:', lead.id);
        checkPotentialClient();
      }, 3000);
      
      return () => {
        clearTimeout(timeoutId);
        clearTimeout(timeoutId2);
        clearTimeout(timeoutId3);
      };
    }
  }, [lead?.id, isOpen, lead]); // Added lead to dependencies to catch any changes

  // Additional effect to ensure state/city are loaded when dropdowns are ready
  useEffect(() => {
    if (lead && formData.country && formData.state && availableStates.length > 0 && selectedCountryCode) {
      // Ensure state is set properly
      const stateMatch = availableStates.find(s => s.name === formData.state || s.name.toLowerCase() === formData.state.toLowerCase());
      if (stateMatch && selectedStateCode !== stateMatch.isoCode) {
        setSelectedStateCode(stateMatch.isoCode);
        const cities = City.getCitiesOfState(selectedCountryCode, stateMatch.isoCode) || [];
        setAvailableCities(cities.map(c => c.name));
      }
    }
    
    if (lead && formData.country && formData.city && availableCities.length > 0 && !availableCities.includes(formData.city)) {
      // City might not be in the list yet, try to load it
      if (selectedStateCode && selectedCountryCode) {
        const cities = City.getCitiesOfState(selectedCountryCode, selectedStateCode) || [];
        if (cities.some(c => c.name === formData.city)) {
          setAvailableCities(cities.map(c => c.name));
        }
      }
    }
  }, [lead, formData.country, formData.state, formData.city, availableStates, availableCities, selectedCountryCode, selectedStateCode]);

  // Handle marking follow-up as completed
  const handleMarkFollowUpCompleted = async (followUpNumber) => {
    if (!lead || !lead.id) {
      alert('Cannot mark follow-up as completed: Lead ID is missing');
      return;
    }

    try {
      setMarkingCompleted(prev => ({ ...prev, [followUpNumber]: true }));
      
      // Send the current date/update from the form so the backend can create
      // the follow_up row if the user pressed Done before saving the form.
      const dateKey = `follow_up_date_${followUpNumber}`;
      const updateKey = `follow_up_${followUpNumber}_update`;
      const result = await leadsAPI.markCompleted(lead.id, followUpNumber, {
        followUpDate: formData[dateKey] || null,
        followUpUpdate: formData[updateKey] || ''
      });
      
      if (result && result.success) {
        // Mark as completed in state immediately
        setFollowUpStatuses(prev => ({ ...prev, [followUpNumber]: true }));
        // Refresh the lead data to show updated follow-up status
        const updatedLead = await leadsAPI.getById(lead.id);
        if (updatedLead && updatedLead.data) {
          // Update the form data with the refreshed lead
          const formatDate = (dateString) => {
            if (!dateString) return '';
            if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
              return dateString;
            }
            const date = new Date(dateString + 'T00:00:00');
            return date.toISOString().split('T')[0];
          };
          
          // getLeadById overwrites follow_up_status with the aggregate. Use
          // the per-row follow_up_status_1 for FU1, not the aggregate.
          const fu1Status = updatedLead.data.follow_up_status_1 || '';
          setFormData(prev => ({
            ...prev,
            follow_up_date_1: formatDate(updatedLead.data.follow_up_date_1),
            follow_up_1_update: updatedLead.data.follow_up_1_update || '',
            follow_up_date_2: formatDate(updatedLead.data.follow_up_date_2),
            follow_up_2_update: updatedLead.data.follow_up_2_update || '',
            follow_up_date_3: formatDate(updatedLead.data.follow_up_date_3),
            follow_up_3_update: updatedLead.data.follow_up_3_update || '',
            follow_up_status: updatedLead.data.follow_up_status || 'Upcoming',
            follow_up_status_1: fu1Status,
            follow_up_status_2: updatedLead.data.follow_up_status_2 || '',
            follow_up_status_3: updatedLead.data.follow_up_status_3 || ''
          }));

          // Do NOT overwrite the whole followUpStatuses object here — the
          // click handler already set `[followUpNumber]: true` above, and a
          // full overwrite using the refetched lead can briefly flip other
          // buttons back to green if the read races the commit. Merge only
          // the clicked row to stay consistent with the DB.
          setFollowUpStatuses(prev => ({ ...prev, [followUpNumber]: true }));
        }
        
        // Trigger refresh in FollowUpTracker and other components
        setTimeout(() => {
          if (window.followUpsRefresh) {
            window.followUpsRefresh();
          }
          // Also trigger refresh in App.js if available
          if (window.fetchFollowUps) {
            window.fetchFollowUps();
          }
          if (window.fetchLeads) {
            window.fetchLeads();
          }
        }, 300);
      } else {
        throw new Error(result?.error || 'Failed to mark follow-up as completed');
      }
    } catch (error) {
      console.error('Error marking follow-up as completed:', error);
      alert('Error marking follow-up as completed: ' + (error.response?.data?.error || error.message));
    } finally {
      setMarkingCompleted(prev => ({ ...prev, [followUpNumber]: false }));
    }
  };

  // Check if all follow-ups are used
  const allFollowUpsUsed = formData.follow_up_date_1 && formData.follow_up_date_2 && formData.follow_up_date_3;

  // Handle clearing follow-ups and saving notes to Company Overview
  const handleRotateFollowUps = () => {
    // Build the history notes from ALL existing follow-ups
    const historyNotes = [];
    
    if (formData.follow_up_date_1) {
      const note1 = formData.follow_up_1_update ? `: ${formData.follow_up_1_update}` : '';
      historyNotes.push(`• Follow-up 1 [${formData.follow_up_date_1}]${note1}`);
    }
    if (formData.follow_up_date_2) {
      const note2 = formData.follow_up_2_update ? `: ${formData.follow_up_2_update}` : '';
      historyNotes.push(`• Follow-up 2 [${formData.follow_up_date_2}]${note2}`);
    }
    if (formData.follow_up_date_3) {
      const note3 = formData.follow_up_3_update ? `: ${formData.follow_up_3_update}` : '';
      historyNotes.push(`• Follow-up 3 [${formData.follow_up_date_3}]${note3}`);
    }
    
    // Append to company_overview
    const existingNotes = formData.company_overview || '';
    const today = new Date().toISOString().split('T')[0];
    const separator = existingNotes ? '\n\n' : '';
    const header = `--- Follow-up History (Cleared on ${today}) ---`;
    const updatedNotes = existingNotes + separator + header + '\n' + historyNotes.join('\n');
    
    // Clear ALL follow-up fields - make them available for new scheduling
    setFormData(prev => ({
      ...prev,
      company_overview: updatedNotes,
      follow_up_date_1: '',
      follow_up_1_update: '',
      follow_up_date_2: '',
      follow_up_2_update: '',
      follow_up_date_3: '',
      follow_up_3_update: '',
      follow_up_status: 'Upcoming'
    }));
    
    // Reset follow-up statuses
    setFollowUpStatuses({
      1: false,
      2: false,
      3: false
    });
    
    alert('All follow-up notes have been saved to Company Overview. All 3 follow-up slots are now available for new scheduling!');
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Double-submit guard. The parent's `loading` prop only flips true AFTER
    // React re-renders, so two rapid clicks (or Enter + click) both fired
    // through to leadsAPI.create() and produced duplicate leads in the
    // Activity tab. A synchronous ref blocks the second call before any
    // network request goes out.
    if (submittingRef.current) {
      console.warn('LeadModal: ignoring duplicate submit');
      return;
    }
    submittingRef.current = true;
    setTimeout(() => { submittingRef.current = false; }, 3000);

    // Handle custom lead source logic
    let finalLeadSource = formData.lead_source;
    if (formData.lead_source === 'Other' && formData.custom_lead_source.trim()) {
      finalLeadSource = formData.custom_lead_source.trim();
    }
    
    // Auto-fill location field from city and country for backward compatibility
    const dataToSubmit = {
      ...formData,
      capacity_mw: formData.capacity_mw === '' ? null : Number(formData.capacity_mw),
      bess_capacity_mw: formData.bess_capacity_mw === '' || formData.bess_capacity_mw == null
        ? null
        : Number(formData.bess_capacity_mw),
      residential_installations: formData.residential_installations === '' || formData.residential_installations == null
        ? null
        : Math.max(0, Math.trunc(Number(formData.residential_installations))),
      ev_charging_assets: formData.ev_charging_assets === '' || formData.ev_charging_assets == null
        ? null
        : Math.max(0, Math.trunc(Number(formData.ev_charging_assets))),
      company_generic_number: typeof formData.company_generic_number === 'string'
        ? (formData.company_generic_number.trim() || null)
        : (formData.company_generic_number || null),
      lead_source: finalLeadSource,
      state: formData.state || '',
      location: formData.city && formData.country 
        ? `${formData.city}${formData.state ? ', ' + formData.state : ''}, ${formData.country}` 
        : formData.city || formData.country || '',
      // Ensure contact_book is normalized to numbered format (1. Name, email) before save
      contact_book: typeof formData.contact_book === 'string' ? normalizeContactBook(formData.contact_book.trim()) || '' : '',
      // Ensure LinkedIn Activity fields are numbers
      poc_shortlisted: parseInt(formData.poc_shortlisted) || 0,
      email_sent: parseInt(formData.email_sent) || 0,
      new_connection: parseInt(formData.new_connection) || 0,
      auto_reply_received: parseInt(formData.auto_reply_received) || 0
    };
    
    // Saving in the second before the server's copy arrives must not wipe the
    // columns this form has not been handed yet. Leaving the key out entirely
    // means the update simply does not touch that column.
    if (lead && lead.id && !hydratedRef.current) {
      ['contact_book', 'company_overview', 'ai_chat_url'].forEach(field => {
        if (!String(formData[field] ?? '').trim()) delete dataToSubmit[field];
      });
    }

    console.log('LeadModal: Submitting form data:', dataToSubmit);
    console.log('LeadModal: contact_book being sent:', dataToSubmit.contact_book ? `"${dataToSubmit.contact_book.substring(0, 80)}${dataToSubmit.contact_book.length > 80 ? '...' : ''}" (length ${dataToSubmit.contact_book.length})` : '(empty)');
    console.log('LeadModal: Location fields being sent:', {
      country: dataToSubmit.country,
      state: dataToSubmit.state,
      city: dataToSubmit.city,
      location: dataToSubmit.location
    });
    console.log('LeadModal: POC fields being sent:', {
      poc_name: dataToSubmit.poc_name,
      email: dataToSubmit.email,
      designation: dataToSubmit.designation,
      contact_no: dataToSubmit.contact_no,
      poc_replied: dataToSubmit.poc_replied,
      poc2_poc_replied: dataToSubmit.poc2_poc_replied,
      poc3_poc_replied: dataToSubmit.poc3_poc_replied,
      poc4_poc_replied: dataToSubmit.poc4_poc_replied
    });
    onSubmit(dataToSubmit);
    
    // Re-check potential client status after form submission to ensure state persists
    if (lead && lead.id) {
      setTimeout(async () => {
        try {
          const checkResult = await dashboardAPI.checkLeadInPotentialClients(lead.id);
          const isInPC = checkResult.isInPotentialClients === true || (checkResult.data && checkResult.data.id) ? true : false;
          const isInOC = checkResult.isInOnboardedClients === true;
          setIsInOnboardedClients(isInOC);
          setIsInPotentialClients(isInPC);
          if (checkResult.data && checkResult.data.id) {
            setPotentialClientId(checkResult.data.id);
            setMeetingSummaryFile(prev => checkResult.data.meeting_summary_file || prev || null);
          }
        } catch (error) {
          console.error('Error re-checking potential client after form save:', error);
          // Don't reset state on error - keep current state
        }
      }, 1000);
    }
  };

  const addPoc = () => {
    if (visiblePocCount < 4) {
      setVisiblePocCount(prev => prev + 1);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, tagName } = e.target;
    let processedValue = value;
    
    // For URL fields, make first character lowercase
    if (name === 'company_url' || name === 'linkedin_url' || name === 'ai_chat_url' ||
        name === 'poc_linkedin_url' || name === 'poc2_linkedin_url' ||
        name === 'poc3_linkedin_url' || name === 'poc4_linkedin_url') {
      if (value.length > 0) {
        processedValue = value.charAt(0).toLowerCase() + value.slice(1);
      }
    } else if (name === 'contact_book') {
      // Don't modify contact_book (emails) - preserve exact input
      processedValue = value;
    } else {
      // Auto-capitalize first character for most text inputs while typing.
      const skipTypes = new Set(['email', 'number', 'date', 'checkbox', 'radio', 'select-one', 'select-multiple']);
      const isTextarea = tagName === 'TEXTAREA';
      const isTextLike = (!skipTypes.has(type) && typeof value === 'string') || isTextarea;
      if (isTextLike && value.length > 0) {
        processedValue = value.charAt(0).toUpperCase() + value.slice(1);
      }
    }
    setFormData(prev => ({ ...prev, [name]: processedValue }));
  };

  // Keep refs in sync so click-outside handler can read latest typed values
  countrySearchRef.current = countrySearchQuery;
  stateSearchRef.current = stateSearchQuery;
  citySearchRef.current = citySearchQuery;

  // Close dropdowns when clicking outside; save any typed country/state/city so location tagging works
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.custom-dropdown')) {
        const c = (countrySearchRef.current || '').trim();
        const s = (stateSearchRef.current || '').trim();
        const t = (citySearchRef.current || '').trim();
        if (countryDropdownOpen && c.length >= 2) {
          setFormData(prev => ({ ...prev, country: c }));
        }
        if (stateDropdownOpen && s.length >= 2) {
          setFormData(prev => ({ ...prev, state: s }));
        }
        if (cityDropdownOpen && t.length >= 2) {
          setFormData(prev => ({ ...prev, city: t }));
        }
        setCountryDropdownOpen(false);
        setCityDropdownOpen(false);
        setStateDropdownOpen(false);
      }
    };

    if (countryDropdownOpen || cityDropdownOpen || stateDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [countryDropdownOpen, cityDropdownOpen, stateDropdownOpen]);

  const handleCountrySelect = (countryName) => {
    const match = availableCountries.find(c => c.name === countryName);
    const isoCode = match?.isoCode || '';
    setFormData(prev => ({ ...prev, country: countryName, state: '', city: '' }));
    setSelectedCountryCode(isoCode);
    setCountryDropdownOpen(false);
    setCountrySearchQuery('');
    setCitySearchQuery('');
    setStateSearchQuery('');
  };

  const handleCitySelect = (city) => {
    setFormData(prev => ({ ...prev, city }));
    setCityDropdownOpen(false);
    setCitySearchQuery('');
  };

  const handleStateSelect = (state) => {
    setFormData(prev => ({ ...prev, state: state.name, city: '' }));
    setSelectedStateCode(state.isoCode);
    setStateDropdownOpen(false);
    setStateSearchQuery('');
    setCitySearchQuery('');
    // Load cities for the selected state
    if (selectedCountryCode && state.isoCode) {
      const cities = City.getCitiesOfState(selectedCountryCode, state.isoCode) || [];
      setAvailableCities(cities.map(c => c.name));
    }
  };


  if (!isOpen) return null;

  const wrapperClass = inline
    ? 'bg-white rounded-lg w-full max-w-4xl mx-auto flex flex-col flex-1 min-h-0'
    : 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4';
  const innerClass = inline
    ? 'w-full flex flex-col flex-1 min-h-0'
    : 'bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto relative';

  return (
    <div className={wrapperClass}>
      <div className={innerClass}>
        {/* Fixed Header - Back + company name always visible */}
        <div className={`flex-shrink-0 bg-white z-10 border-b border-gray-200 p-4 flex items-center justify-between ${inline ? '' : 'sticky top-0'}`}>
          {inline ? (
            <button type="button" onClick={onClose} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
              Back
            </button>
          ) : (
            <div className="w-8" />
          )}
          <h2 className="text-xl font-bold">
            {lead ? (
              <>Edit Lead - <span className="text-[#679632]">{lead.company_name || formData.company_name}</span></>
            ) : (
              'Add New Lead'
            )}
          </h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => formRef.current?.requestSubmit()}
              className="p-2 rounded-lg text-[#679632] hover:bg-[#679632]/10 transition-colors"
              title="Save lead (Cmd/Ctrl+S)"
              aria-label="Save lead"
            >
              <Save size={20} />
            </button>
            {inline && lead && (
              <button
                type="button"
                onClick={() => setShowEmailsPanel(true)}
                className="p-2 rounded-lg text-[#679632] hover:bg-[#679632]/10 transition-colors"
                title="Send follow-up emails"
                aria-label="Send follow-up emails"
              >
                <Mail size={22} />
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className={inline ? 'invisible w-8' : 'text-gray-400 hover:text-gray-600'}
            >
              <X size={24} />
            </button>
          </div>
        </div>
        <FollowUpEmailsPanel
          isOpen={showEmailsPanel}
          onClose={() => setShowEmailsPanel(false)}
          lead={lead}
          formData={formData}
        />
        <div className={`p-6 pt-4 ${inline ? 'flex-1 overflow-y-auto min-h-0' : ''}`}>
          <form ref={formRef} onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Lead Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="lead_date"
                  value={formData.lead_date}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company URL</label>
                <div className="flex items-center gap-2">
                  <input
                    type="url"
                    name="company_url"
                    value={formData.company_url}
                    onChange={handleChange}
                    placeholder="https://example.com"
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                  />
                  {formData.company_url && (
                    <a
                      href={formData.company_url.startsWith('http') ? formData.company_url : `https://${formData.company_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center"
                      title="Open in new tab"
                    >
                      <ExternalLink size={18} />
                    </a>
                  )}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1 gap-2">
                  <label className="block text-sm font-medium text-gray-700">AI Chat URL</label>
                  {/* Small green button beside the heading — generates the AI
                      company-analysis report and drops its link in the field below. */}
                  {lead?.id ? (
                    <button
                      type="button"
                      disabled={generatingAnalysis}
                      onClick={async () => {
                        setGeneratingAnalysis(true);
                        try {
                          let r = await leadsAPI.generateAnalysis(lead.id);
                          // A report from the last 30 days is reused (saves AI cost); offer a fresh one.
                          if (r?.success && r.cached) {
                            const when = r.generatedAt ? new Date(r.generatedAt).toLocaleDateString() : 'recently';
                            if (window.confirm(`A report for this company was made on ${when}. Click OK to research it again (1–3 min), or Cancel to open the saved report.`)) {
                              r = await leadsAPI.generateAnalysis(lead.id, { force: true });
                            }
                          }
                          if (r?.success && r.url) {
                            setFormData(f => ({ ...f, ai_chat_url: r.url }));
                            window.open(r.url, '_blank');
                          } else {
                            alert(`Could not generate analysis: ${r?.error || 'unknown error'}`);
                          }
                        } catch (e) {
                          alert(`Could not generate analysis: ${e.response?.data?.error || e.message}`);
                        } finally { setGeneratingAnalysis(false); }
                      }}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-[#679632] text-white rounded-md hover:bg-[#587f2b] disabled:opacity-50 text-xs font-medium whitespace-nowrap"
                      title="Research this company with AI and put a report link in the field below (1–3 min)"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" /></svg>
                      {generatingAnalysis ? 'Researching… (1–3 min)' : 'Generate AI analysis'}
                    </button>
                  ) : (
                    <span className="text-xs text-gray-400">Save lead to generate</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="url"
                    name="ai_chat_url"
                    value={formData.ai_chat_url}
                    onChange={handleChange}
                    placeholder="https://chat.openai.com/share/..."
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                  />
                  {formData.ai_chat_url && (
                    <a
                      href={formData.ai_chat_url.startsWith('http') ? formData.ai_chat_url : `https://${formData.ai_chat_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center"
                      title="Open in new tab"
                    >
                      <ExternalLink size={18} />
                    </a>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">LinkedIn URL</label>
                <div className="flex items-center gap-2">
                  <input
                    type="url"
                    name="linkedin_url"
                    value={formData.linkedin_url}
                    onChange={handleChange}
                    placeholder="https://linkedin.com/company/example"
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                  />
                  {formData.linkedin_url && (
                    <a
                      href={formData.linkedin_url.startsWith('http') ? formData.linkedin_url : `https://${formData.linkedin_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center"
                      title="Open in new tab"
                    >
                      <ExternalLink size={18} />
                    </a>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Core Business</label>
                <select name="core_business" value={formData.core_business} onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent">
                  <option value="">Select...</option>
                  <option value="Developer">Developer</option>
                  <option value="Asset Manager">Asset Manager</option>
                  <option value="O&M">O&M</option>
                  <option value="EPC">EPC</option>
                  <option value="Investor">Investor</option>
                  <option value="Integrated">Integrated</option>
                  <option value="BESS">BESS</option>
                  <option value="Residential">Residential</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  PV Capacity (MW)
                </label>
                <input
                  type="number"
                  name="capacity_mw"
                  value={formData.capacity_mw}
                  onChange={handleChange}
                  placeholder="Enter PV capacity in MW"
                  min="0"
                  step="0.01"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  BESS Capacity (MW)
                </label>
                <input
                  type="number"
                  name="bess_capacity_mw"
                  value={formData.bess_capacity_mw}
                  onChange={handleChange}
                  placeholder="Enter BESS capacity in MW"
                  min="0"
                  step="0.01"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Residential Installations
                </label>
                <input
                  type="number"
                  name="residential_installations"
                  value={formData.residential_installations}
                  onChange={handleChange}
                  placeholder="Number of residential installations"
                  min="0"
                  step="1"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Number of EV Charging Assets
                </label>
                <input
                  type="number"
                  name="ev_charging_assets"
                  value={formData.ev_charging_assets}
                  onChange={handleChange}
                  placeholder="Number of EV charging assets"
                  min="0"
                  step="1"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company Generic Number
                </label>
                <PhoneInput
                  value={formData.company_generic_number}
                  onChange={(v) => setFormData(prev => ({ ...prev, company_generic_number: v }))}
                  placeholder="e.g. 80 1234 5678"
                />
              </div>

              {(() => {
                const filteredCountries = availableCountries
                  .slice()
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map(c => c.name)
                  .filter(name => name.toLowerCase().includes(countrySearchQuery.toLowerCase()));
                return (
                  <div className="custom-dropdown relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                    <div
                      onClick={() => setCountryDropdownOpen(!countryDropdownOpen)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent cursor-pointer bg-white flex items-center justify-between"
                    >
                      {countryDropdownOpen ? (
                        <input
                          ref={countryInputRef}
                          type="text"
                          value={countrySearchQuery}
                          onChange={(e) => setCountrySearchQuery(capitalizeFirst(e.target.value))}
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => navigateList(
                            e,
                            filteredCountries.length,
                            countryHighlight,
                            setCountryHighlight,
                            (idx) => handleCountrySelect(filteredCountries[idx]),
                            () => setCountryDropdownOpen(false)
                          )}
                          placeholder={formData.country || 'Select Country'}
                          className="w-full outline-none text-gray-900 placeholder-gray-400 bg-white"
                        />
                      ) : (
                        <>
                          <span className={formData.country ? 'text-[#679632] font-medium' : 'text-gray-400'}>
                            {formData.country || 'Select Country'}
                          </span>
                          <svg className={`w-4 h-4 text-gray-400 transition-transform ${countryDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </>
                      )}
                    </div>
                    {countryDropdownOpen && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                        <div className="max-h-48 overflow-y-auto">
                          {filteredCountries.length === 0 && (
                            <div className="px-3 py-2 text-gray-400 text-sm italic">No matches</div>
                          )}
                          {filteredCountries.map((country, idx) => (
                            <div
                              key={country}
                              ref={el => { if (el && idx === countryHighlight) el.scrollIntoView({ block: 'nearest' }); }}
                              onMouseEnter={() => setCountryHighlight(idx)}
                              onClick={() => handleCountrySelect(country)}
                              className={`px-3 py-2 cursor-pointer ${idx === countryHighlight ? 'bg-[#679632] text-white' : (formData.country === country ? 'bg-[#76AB3F] text-white font-medium' : 'text-gray-900')}`}
                            >
                              {country}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {(() => {
                const filteredStates = availableStates.filter(s => s.name.toLowerCase().includes(stateSearchQuery.toLowerCase()));
                return (
                  <div className="custom-dropdown relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">State / Province</label>
                    <div
                      onClick={() => { if (formData.country && availableStates.length > 0) setStateDropdownOpen(!stateDropdownOpen); }}
                      className={`w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent cursor-pointer bg-white flex items-center justify-between ${!formData.country || availableStates.length === 0 ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    >
                      {stateDropdownOpen ? (
                        <input
                          ref={stateInputRef}
                          type="text"
                          value={stateSearchQuery}
                          onChange={(e) => setStateSearchQuery(capitalizeFirst(e.target.value))}
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => navigateList(
                            e,
                            filteredStates.length,
                            stateHighlight,
                            setStateHighlight,
                            (idx) => handleStateSelect(filteredStates[idx]),
                            () => setStateDropdownOpen(false)
                          )}
                          placeholder={formData.state || 'Select State'}
                          className="w-full outline-none text-gray-900 placeholder-gray-400 bg-white"
                          disabled={availableStates.length === 0}
                        />
                      ) : (
                        <>
                          <span className={formData.state ? 'text-[#679632] font-medium' : 'text-gray-400'}>
                            {formData.state || 'Select State'}
                          </span>
                          <svg className={`w-4 h-4 text-gray-400 transition-transform ${stateDropdownOpen ? 'rotate-180' : ''} ${availableStates.length === 0 ? 'opacity-50' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </>
                      )}
                    </div>
                    {stateDropdownOpen && availableStates.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                        <div className="max-h-48 overflow-y-auto">
                          {filteredStates.length === 0 && (
                            <div className="px-3 py-2 text-gray-400 text-sm italic">No matches</div>
                          )}
                          {filteredStates.map((s, idx) => (
                            <div
                              key={s.isoCode}
                              ref={el => { if (el && idx === stateHighlight) el.scrollIntoView({ block: 'nearest' }); }}
                              onMouseEnter={() => setStateHighlight(idx)}
                              onClick={() => handleStateSelect(s)}
                              className={`px-3 py-2 cursor-pointer ${idx === stateHighlight ? 'bg-[#679632] text-white' : (formData.state === s.name ? 'bg-[#76AB3F] text-white font-medium' : 'text-gray-900')}`}
                            >
                              {s.name}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {(() => {
                const cityQuery = citySearchQuery.trim();
                const filteredCities = availableCities.filter(cty => cty.toLowerCase().includes(citySearchQuery.toLowerCase()));
                // Allow a free-text city that isn't in the country-state-city dataset
                // (e.g. smaller towns the library doesn't cover). When the typed value
                // has no exact match, offer it as a custom pickable option.
                const hasExactCity = availableCities.some(cty => cty.toLowerCase() === cityQuery.toLowerCase());
                const showCustomCity = cityQuery.length > 0 && !hasExactCity;
                const cityPickList = showCustomCity ? [...filteredCities, cityQuery] : filteredCities;
                return (
                  <div className="custom-dropdown relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <div
                      onClick={() => formData.country && setCityDropdownOpen(!cityDropdownOpen)}
                      className={`w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent cursor-pointer bg-white flex items-center justify-between ${!formData.country ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    >
                      {cityDropdownOpen ? (
                        <input
                          ref={cityInputRef}
                          type="text"
                          value={citySearchQuery}
                          onChange={(e) => setCitySearchQuery(capitalizeFirst(e.target.value))}
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => navigateList(
                            e,
                            cityPickList.length,
                            cityHighlight,
                            setCityHighlight,
                            (idx) => handleCitySelect(cityPickList[idx]),
                            () => setCityDropdownOpen(false)
                          )}
                          placeholder={formData.city || (formData.country ? 'Select or type a city' : 'Select Country First')}
                          className="w-full outline-none text-gray-900 placeholder-gray-400 bg-white"
                          disabled={!formData.country}
                        />
                      ) : (
                        <>
                          <span className={formData.city ? 'text-[#679632] font-medium' : 'text-gray-400'}>
                            {formData.city || (formData.country ? 'Select City' : 'Select Country First')}
                          </span>
                          <svg className={`w-4 h-4 text-gray-400 transition-transform ${cityDropdownOpen ? 'rotate-180' : ''} ${!formData.country ? 'opacity-50' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </>
                      )}
                    </div>
                    {cityDropdownOpen && formData.country && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                        <div className="max-h-48 overflow-y-auto">
                          {filteredCities.length === 0 && !showCustomCity && (
                            <div className="px-3 py-2 text-gray-400 text-sm italic">
                              {availableCities.length === 0 ? 'Type a city name' : 'No matches'}
                            </div>
                          )}
                          {filteredCities.map((city, idx) => (
                            <div
                              key={city}
                              ref={el => { if (el && idx === cityHighlight) el.scrollIntoView({ block: 'nearest' }); }}
                              onMouseEnter={() => setCityHighlight(idx)}
                              onClick={() => handleCitySelect(city)}
                              className={`px-3 py-2 cursor-pointer ${idx === cityHighlight ? 'bg-[#679632] text-white' : (formData.city === city ? 'bg-[#76AB3F] text-white font-medium' : 'text-gray-900')}`}
                            >
                              {city}
                            </div>
                          ))}
                          {showCustomCity && (() => {
                            const customIdx = cityPickList.length - 1;
                            return (
                              <div
                                key="__custom_city__"
                                ref={el => { if (el && customIdx === cityHighlight) el.scrollIntoView({ block: 'nearest' }); }}
                                onMouseEnter={() => setCityHighlight(customIdx)}
                                onClick={() => handleCitySelect(cityQuery)}
                                className={`px-3 py-2 cursor-pointer border-t border-gray-100 ${customIdx === cityHighlight ? 'bg-[#679632] text-white' : 'text-gray-900'}`}
                              >
                                Use "{cityQuery}"
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">POC Name</label>
                <input type="text" name="poc_name" value={formData.poc_name} onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Designation</label>
                <input type="text" name="designation" value={formData.designation} onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input type="email" name="email" value={formData.email} onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact No.</label>
                <PhoneInput value={formData.contact_no}
                  onChange={(v) => setFormData(prev => ({ ...prev, contact_no: v }))} />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">POC LinkedIn URL</label>
                <input
                  type="url"
                  name="poc_linkedin_url"
                  value={formData.poc_linkedin_url}
                  onChange={handleChange}
                  placeholder="https://www.linkedin.com/in/..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Lead Source</label>
                <select
                  name="lead_source"
                  value={formData.lead_source}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                >
                  <option value="">Select Lead Source</option>
                  <option value="LinkedIn">LinkedIn</option>
                  <option value="Email">Email</option>
                  <option value="Referral">Referral</option>
                  <option value="Website">Website</option>
                  <option value="Cold Call">Cold Call</option>
                  <option value="Event">Event</option>
                  <option value="Other">Other</option>
                </select>
                
                {/* Custom Lead Source Field - Only show when "Other" is selected */}
                {formData.lead_source === 'Other' && (
                  <div className="mt-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Custom Lead Source</label>
                    <input
                      type="text"
                      name="custom_lead_source"
                      value={formData.custom_lead_source}
                      onChange={handleChange}
                      placeholder="Enter custom lead source name"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                    />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Stage</label>
                <select name="stage" value={formData.stage} onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent">
                  <option value="New">New</option>
                  <option value="Contacted">Contacted</option>
                  <option value="Initial call">Initial call</option>
                  <option value="Demo call">Demo call</option>
                  <option value="Follow-up 1">Follow-up 1</option>
                  <option value="Follow-up 2">Follow-up 2</option>
                  <option value="Proposal">Proposal</option>
                  <option value="Negotiation">Negotiation</option>
                  <option value="Onboarded">Onboarded</option>
                  <option value="Important - But Contact Later">Important - But Contact Later</option>
                  <option value="Deferred">Deferred</option>
                </select>
              </div>

              {/* Client interest — a simple toggle. Click a button to set it;
                  click the active one again to clear back to "not set". */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client Interest</label>
                <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setFormData(f => ({ ...f, interest_status: f.interest_status === 'Interested' ? '' : 'Interested' }))}
                    className={`px-4 py-2 text-sm font-medium transition-colors ${formData.interest_status === 'Interested' ? 'bg-[#679632] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  >
                    Interested
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(f => ({ ...f, interest_status: f.interest_status === 'Not Interested' ? '' : 'Not Interested' }))}
                    className={`px-4 py-2 text-sm font-medium border-l border-gray-300 transition-colors ${formData.interest_status === 'Not Interested' ? 'bg-red-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  >
                    Not Interested
                  </button>
                </div>
                {formData.interest_status && (
                  <button type="button" onClick={() => setFormData(f => ({ ...f, interest_status: '' }))}
                    className="ml-2 text-xs text-gray-400 hover:text-gray-600">clear</button>
                )}
              </div>

              {/* Meeting Summary Files — multi-file upload with per-file delete.
                  Files live on the lead (leads.meeting_summary_files); the array
                  is the source of truth. Falls back to the single legacy /
                  potential-client file when the array is empty. */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Meeting Summary Files {isInPotentialClients && <span className="text-green-600 text-xs">(Potential Client)</span>}
                </label>
                {(() => {
                  // Display list: prefer the lead-level array; otherwise fall back
                  // to the single legacy/PC file so nothing already attached is hidden.
                  const summaryFiles = (meetingSummaryFiles && meetingSummaryFiles.length)
                    ? meetingSummaryFiles
                    : (meetingSummaryFile ? [{ url: meetingSummaryFile, name: null }] : []);

                  const fileLabel = (f) => {
                    if (f.name) return f.name;
                    try {
                      const parts = decodeURIComponent(new URL(f.url).pathname).split('/');
                      return (parts[parts.length - 1] || '').replace(/^\d+_/, '') || 'File attached';
                    } catch { return 'File attached'; }
                  };

                  return (
                    <div className="space-y-2">
                      {summaryFiles.map((f, idx) => (
                        <div key={f.url || idx} className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 bg-gray-50">
                          <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                          <span className="text-sm text-gray-700 truncate flex-1" title={fileLabel(f)}>{fileLabel(f)}</span>
                          <a
                            href={toViewableUrl(typeof f.url === 'string' && f.url.startsWith('http')
                              ? f.url
                              : dashboardAPI.getMeetingSummaryUrl(potentialClientId))}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 text-xs font-medium flex-shrink-0"
                          >
                            View
                          </a>
                          <button
                            type="button"
                            disabled={deletingFileUrl === f.url}
                            onClick={async () => {
                              if (!lead?.id) { alert('Save the lead first.'); return; }
                              if (!window.confirm('Delete this file? This cannot be undone.')) return;
                              try {
                                setDeletingFileUrl(f.url);
                                const result = await leadsAPI.deleteMeetingSummary(lead.id, f.url);
                                if (result.success) {
                                  const files = result.data?.files || [];
                                  setMeetingSummaryFiles(files);
                                  setMeetingSummaryFile(files[0]?.url || null);
                                } else {
                                  alert('Error deleting file: ' + (result.error || 'Unknown error'));
                                }
                              } catch (error) {
                                console.error('Error deleting file:', error);
                                alert('Error deleting file. Please try again.');
                              } finally {
                                setDeletingFileUrl(null);
                              }
                            }}
                            className="text-red-600 hover:text-red-800 text-xs font-medium flex-shrink-0 disabled:opacity-50"
                          >
                            {deletingFileUrl === f.url ? 'Deleting…' : 'Delete'}
                          </button>
                        </div>
                      ))}

                      {/* Add another file (append). Always available once the lead is saved. */}
                      <label className="flex items-center gap-2 w-full border border-gray-300 rounded-lg px-3 py-2 bg-white text-gray-500 cursor-pointer text-sm hover:bg-gray-50">
                        {uploadingFile
                          ? 'Uploading...'
                          : (summaryFiles.length ? 'Add another file 📁' : 'Upload Meeting Summary 📁')}
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (!file) return;
                            if (!lead?.id) { alert('Save the lead first, then upload the file.'); return; }
                            try {
                              setUploadingFile(true);
                              // Lead-level upload — APPENDS to the file list.
                              const result = await leadsAPI.uploadMeetingSummary(lead.id, file);
                              if (result.success) {
                                const files = result.data?.files || [];
                                setMeetingSummaryFiles(files);
                                setMeetingSummaryFile(files[0]?.url || result.data?.url || meetingSummaryFile);
                              } else {
                                alert('Error uploading file: ' + (result.error || 'Unknown error'));
                              }
                            } catch (error) {
                              console.error('Error uploading file:', error);
                              alert('Error uploading file. Please try again.');
                            } finally {
                              setUploadingFile(false);
                              e.target.value = '';
                            }
                          }}
                          className="hidden"
                          disabled={uploadingFile}
                        />
                      </label>
                      <p className="text-xs text-gray-500 mt-1">PDF, DOC, DOCX, TXT, PNG, JPG (max 25MB) · multiple files supported</p>
                    </div>
                  );
                })()}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-0.5">Priority</label>
                <select name="priority" value={formData.priority} onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent">
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </select>
              </div>

              {/* Action Buttons - Add to Potential Clients and Close Deal */}
              <div className="flex flex-col gap-2">
                {lead && onConvertToPotentialClient && (
                  <button
                    type="button"
                    disabled={isInPotentialClients || isInOnboardedClients}
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      
                      // Double-check state before proceeding - also do a fresh API check
                      if (isInPotentialClients || isInOnboardedClients) {
                        console.log('⚠️ Button clicked but isInPotentialClients or isInOnboardedClients is true, blocking action');
                        if (isInOnboardedClients) {
                          alert('This company is already in Onboarded Clients.');
                        } else {
                          alert('This company is already in Potential Clients.');
                        }
                        return;
                      }
                      
                      // Do a final check before proceeding
                      try {
                        const finalCheck = await dashboardAPI.checkLeadInPotentialClients(lead.id);
                        if (finalCheck.success && (finalCheck.isInPotentialClients === true || (finalCheck.data && finalCheck.data.id))) {
                          console.log('⚠️ Final check revealed company is already in potential clients');
                          setIsInPotentialClients(true);
                          alert('This company is already in Potential Clients.');
                          return;
                        }
                        if (finalCheck.success && finalCheck.isInOnboardedClients === true) {
                          console.log('⚠️ Final check revealed company is already in onboarded clients');
                          setIsInOnboardedClients(true);
                          alert('This company is already in Onboarded Clients.');
                          return;
                        }
                      } catch (error) {
                        console.error('Error in final check:', error);
                      }
                      
                      if (window.confirm('Are you sure you want to add this company to potential clients?')) {
                        const result = await onConvertToPotentialClient(lead.id);
                        if (result && result.success) {
                          // Immediately set state to disable button
                          setIsInPotentialClients(true);
                          // Re-check from database to get potential client ID
                          setTimeout(async () => {
                            try {
                              const checkResult = await dashboardAPI.checkLeadInPotentialClients(lead.id);
                              if (checkResult.data && checkResult.data.id) {
                                setPotentialClientId(checkResult.data.id);
                                setMeetingSummaryFile(prev => checkResult.data.meeting_summary_file || prev || null);
                              }
                            } catch (error) {
                              console.error('Error re-checking potential client:', error);
                            }
                          }, 300);
                        } else if (result && result.alreadyExists) {
                          // Backend detected duplicate
                          setIsInPotentialClients(true);
                          setTimeout(async () => {
                            try {
                              const checkResult = await dashboardAPI.checkLeadInPotentialClients(lead.id);
                              if (checkResult.data && checkResult.data.id) {
                                setPotentialClientId(checkResult.data.id);
                                setMeetingSummaryFile(prev => checkResult.data.meeting_summary_file || prev || null);
                              }
                            } catch (error) {
                              console.error('Error re-checking potential client:', error);
                            }
                          }, 300);
                        }
                      }
                    }}
                    className={`w-full px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                      (isInPotentialClients || isInOnboardedClients)
                        ? 'bg-gray-400 text-gray-600 cursor-not-allowed opacity-60' 
                        : 'bg-blue-600 text-white hover:bg-blue-700 cursor-pointer'
                    }`}
                    style={(isInPotentialClients || isInOnboardedClients) ? { pointerEvents: 'none' } : {}}
                    title={
                      isInOnboardedClients ? 'Already in Onboarded Clients' :
                      isInPotentialClients ? 'Already in Potential Clients' : 
                      'Add to Potential Clients'
                    }
                    aria-disabled={isInPotentialClients || isInOnboardedClients}
                  >
                    Add to Potential Clients
                  </button>
                )}
                {/* Close Deal button removed - can only close deal from Potential Clients tab */}
                {(isInPotentialClients || isInOnboardedClients) && (
                  <div className="w-full px-3 py-1.5 bg-gray-200 text-gray-600 rounded-lg text-sm font-medium text-center">
                    {isInOnboardedClients ? 'Already Onboarded' : 'In Potential Clients'}
                  </div>
                )}
              </div>

              {/* BDE + Contact book (left, stacked) and Company Overview (right,
                  full height) live in their own 2-col block so the left column
                  fills instead of leaving a gap beside the tall overview box. */}
              <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-2 md:items-start">
              <div className="md:col-start-1 md:row-start-1">
                <label className="block text-sm font-medium text-gray-700 mb-0.5">BDE Name</label>
                <select name="bde_name" value={formData.bde_name} onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent">
                  <option value="">Select...</option>
                  {bdaOptions(bdeUsers).map((bde) => (
                    <option key={bde.id} value={bde.name}>
                      {bde.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-start-2 md:row-start-1 md:row-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-0.5">
                  Company Overview
                  <span className="text-xs text-gray-500 ml-2">
                    ({formData.company_overview?.length || 0}/1000 characters)
                  </span>
                </label>
                <textarea
                  name="company_overview"
                  value={formData.company_overview || ''}
                  onChange={handleChange}
                  maxLength={1000}
                  rows={8}
                  placeholder="Enter company overview, description, or notes (max 1000 characters)"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent resize-y min-h-[10rem]"
                />
                {/* Quick-insert tags — click to toggle a #Hashtag in Company Overview.
                    Hashtag form strips spaces (e.g. "Asset Manager" → "#AssetManager").
                    Clicking a selected chip removes the tag and cleans up the separator. */}
                <div className="mt-2 flex flex-wrap gap-2">
                  <span className="text-xs text-gray-500 self-center mr-1">Quick add:</span>
                  {['Developer', 'Asset Manager', 'EPC', 'O&M', 'Investor', 'IPP', 'BESS', 'Rooftop', 'Residential', 'EV Charging'].map(tag => {
                    const hashtag = '#' + tag.replace(/\s+/g, '');
                    const escapedHashtag = hashtag.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                    const current = formData.company_overview || '';
                    const selected = new RegExp(`(^|\\s|,)${escapedHashtag}(?=$|\\s|,)`, 'i').test(current);

                    const addHashtag = (existing) => {
                      const separator = existing && !existing.match(/[\s,]$/) ? ' ' : '';
                      return (existing + separator + hashtag).slice(0, 1000);
                    };
                    const removeHashtag = (existing) => {
                      // Remove the hashtag and clean up any adjacent separator/whitespace
                      const stripRe = new RegExp(`(,\\s*|\\s+)?${escapedHashtag}(\\s*,|\\s+)?`, 'gi');
                      return existing
                        .replace(stripRe, (_, before, after) => (before && after ? ' ' : ''))
                        .replace(/\s{2,}/g, ' ')
                        .replace(/^[\s,]+|[\s,]+$/g, '');
                    };

                    return (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => {
                          const existing = formData.company_overview || '';
                          const next = selected ? removeHashtag(existing) : addHashtag(existing);
                          setFormData({ ...formData, company_overview: next });
                        }}
                        className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                          selected
                            ? 'bg-[#679632] text-white border-[#679632] hover:bg-[#587f2b]'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-[#679632] hover:text-[#679632]'
                        }`}
                        title={selected ? `Remove ${hashtag} from Company Overview` : `Add ${hashtag} to Company Overview`}
                        aria-pressed={selected}
                      >
                        {tag}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="md:col-start-1 md:row-start-2">
                <div className="flex items-center justify-between gap-2 mb-0.5">
                  <label htmlFor="contact_book" className="block text-sm font-medium text-gray-700">
                    Contact book (emails)
                  </label>
                  <button
                    type="button"
                    onClick={() => { setAddContactName(''); setAddContactEmail(''); setShowAddContactModal(true); }}
                    className="text-sm py-1 px-2 rounded border border-[#679632] text-[#679632] hover:bg-[#679632] hover:text-white transition-colors"
                  >
                    Add contact
                  </button>
                </div>
                <p className="text-xs text-gray-500 mb-0.5">
                  Numbered list (1. Name, email). Add via button for consistent format, or paste. Shown as recipients when you click Scrape All Emails. Each name is used when sending so that email is addressed correctly (e.g. Hi {'{{first_name}}'},).
                </p>
                <textarea
                  id="contact_book"
                  name="contact_book"
                  value={formData.contact_book || ''}
                  onChange={handleChange}
                  rows={12}
                  placeholder={'1. Name, email@company.com\n2. Name, email@company.com'}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent resize-y min-h-[16rem]"
                />
                {showAddContactModal && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4" onClick={() => setShowAddContactModal(false)}>
                    <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-4" onClick={e => e.stopPropagation()}>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Add contact</h3>
                      <p className="text-xs text-gray-500 mb-2">Name and email will be added as the next numbered line. No manual numbering needed.</p>
                      <div className="space-y-2 mb-4">
                        <label className="block text-sm font-medium text-gray-700">Name</label>
                        <input
                          type="text"
                          value={addContactName}
                          onChange={e => setAddContactName(e.target.value)}
                          placeholder="e.g. John Smith"
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                          autoFocus
                        />
                        <label className="block text-sm font-medium text-gray-700">Email</label>
                        <input
                          type="email"
                          value={addContactEmail}
                          onChange={e => setAddContactEmail(e.target.value)}
                          placeholder="john@company.com"
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                        />
                      </div>
                      <div className="flex gap-2 justify-end">
                        <button type="button" onClick={() => setShowAddContactModal(false)} className="px-3 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Cancel</button>
                        <button
                          type="button"
                          onClick={() => {
                            const email = addContactEmail.trim();
                            if (!email || !email.includes('@')) return;
                            const entries = parseContactBook(formData.contact_book || '');
                            entries.push({ name: addContactName.trim(), email });
                            setFormData(prev => ({ ...prev, contact_book: formatContactBook(entries) }));
                            setAddContactName(''); setAddContactEmail(''); setShowAddContactModal(false);
                          }}
                          disabled={!addContactEmail.trim() || !addContactEmail.includes('@')}
                          className="px-3 py-2 bg-[#679632] text-white rounded-lg hover:bg-[#587f2b] disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              </div>
            </div>

            {/* LinkedIn Activity Section */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-md font-semibold text-gray-900 mb-4">LinkedIn Activity Tracking</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">POCs Shortlisted</label>
                  <input
                    type="number"
                    name="poc_shortlisted"
                    value={formData.poc_shortlisted}
                    onChange={handleChange}
                    placeholder="Number of POCs identified"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Emails Sent</label>
                  <input
                    type="number"
                    name="email_sent"
                    value={formData.email_sent}
                    onChange={handleChange}
                    placeholder="Number of emails sent"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">New Connections</label>
                  <input
                    type="number"
                    name="new_connection"
                    value={formData.new_connection}
                    onChange={handleChange}
                    placeholder="New LinkedIn connections"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Auto Replies Received</label>
                  <input
                    type="number"
                    name="auto_reply_received"
                    value={formData.auto_reply_received}
                    onChange={handleChange}
                    placeholder="Automated responses"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Sales Message Sent</label>
                  <input
                    type="date"
                    name="first_sales_message_sent"
                    value={formData.first_sales_message_sent}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Response from POC</label>
                  <select
                    name="response_from_poc"
                    value={formData.response_from_poc}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                  >
                    <option value="">Select...</option>
                    <option value="No Response">No Response</option>
                    <option value="Positive">Positive</option>
                    <option value="Negative">Negative</option>
                    <option value="Pending">Pending</option>
                    <option value="Interested">Interested</option>
                    <option value="Not Interested">Not Interested</option>
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    POC Reply Text <span className="text-gray-500 text-xs">(Max 1000 characters)</span>
                  </label>
                  <textarea
                    name="poc_replied"
                    value={formData.poc_replied}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length <= 1000) {
                        handleChange(e);
                      }
                    }}
                    rows="3"
                    maxLength={1000}
                    placeholder="Enter the actual response text from POC (max 1000 characters)..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                  />
                  <div className="text-xs text-gray-500 mt-1 text-right">
                    {formData.poc_replied.length}/1000 characters
                  </div>
                </div>
              </div>
              
              {/* Add POC Button after POC 1 */}
              {visiblePocCount < 4 && visiblePocCount === 1 && (
                <div className="mt-4 flex justify-center">
                  <button
                    type="button"
                    onClick={addPoc}
                    className="flex items-center gap-2 px-4 py-2 bg-[#679632] text-white rounded-lg hover:bg-[#587f2b] transition-colors font-medium"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add POC 2
                  </button>
                </div>
              )}
            </div>

            {/* POC 2 Section */}
            {visiblePocCount >= 2 && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-md font-semibold text-gray-900 mb-4">POC 2 (Optional)</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">POC 2 Name</label>
                  <input type="text" name="poc2_name" value={formData.poc2_name} onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">POC 2 Designation</label>
                  <input type="text" name="poc2_designation" value={formData.poc2_designation} onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">POC 2 Email</label>
                  <input type="email" name="poc2_email" value={formData.poc2_email} onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">POC 2 Contact No.</label>
                  <PhoneInput value={formData.poc2_contact_no}
                    onChange={(v) => setFormData(prev => ({ ...prev, poc2_contact_no: v }))} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">POC 2 LinkedIn URL</label>
                  <input
                    type="url"
                    name="poc2_linkedin_url"
                    value={formData.poc2_linkedin_url}
                    onChange={handleChange}
                    placeholder="https://www.linkedin.com/in/..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">POC 2 Response</label>
                  <select name="poc2_response_from_poc" value={formData.poc2_response_from_poc} onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent">
                    <option value="">Select...</option>
                    <option value="No Response">No Response</option>
                    <option value="Positive">Positive</option>
                    <option value="Negative">Negative</option>
                    <option value="Pending">Pending</option>
                    <option value="Interested">Interested</option>
                    <option value="Not Interested">Not Interested</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    POC 2 Reply Text <span className="text-gray-500 text-xs">(Max 1000 characters)</span>
                  </label>
                  <textarea
                    name="poc2_poc_replied"
                    value={formData.poc2_poc_replied}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length <= 1000) {
                        handleChange(e);
                      }
                    }}
                    rows="3"
                    maxLength={1000}
                    placeholder="Enter the actual response text from POC 2 (max 1000 characters)..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                  />
                  <div className="text-xs text-gray-500 mt-1 text-right">
                    {formData.poc2_poc_replied.length}/1000 characters
                  </div>
                </div>
              </div>
              
              {/* Add POC Button after POC 2 */}
              {visiblePocCount < 4 && visiblePocCount === 2 && (
                <div className="mt-4 flex justify-center">
                  <button
                    type="button"
                    onClick={addPoc}
                    className="flex items-center gap-2 px-4 py-2 bg-[#679632] text-white rounded-lg hover:bg-[#587f2b] transition-colors font-medium"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add POC 3
                  </button>
                </div>
              )}
            </div>
            )}

            {/* POC 3 Section */}
            {visiblePocCount >= 3 && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-md font-semibold text-gray-900 mb-4">POC 3 (Optional)</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">POC 3 Name</label>
                  <input type="text" name="poc3_name" value={formData.poc3_name} onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">POC 3 Designation</label>
                  <input type="text" name="poc3_designation" value={formData.poc3_designation} onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">POC 3 Email</label>
                  <input type="email" name="poc3_email" value={formData.poc3_email} onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">POC 3 Contact No.</label>
                  <PhoneInput value={formData.poc3_contact_no}
                    onChange={(v) => setFormData(prev => ({ ...prev, poc3_contact_no: v }))} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">POC 3 LinkedIn URL</label>
                  <input
                    type="url"
                    name="poc3_linkedin_url"
                    value={formData.poc3_linkedin_url}
                    onChange={handleChange}
                    placeholder="https://www.linkedin.com/in/..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">POC 3 Response</label>
                  <select name="poc3_response_from_poc" value={formData.poc3_response_from_poc} onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent">
                    <option value="">Select...</option>
                    <option value="No Response">No Response</option>
                    <option value="Positive">Positive</option>
                    <option value="Negative">Negative</option>
                    <option value="Pending">Pending</option>
                    <option value="Interested">Interested</option>
                    <option value="Not Interested">Not Interested</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    POC 3 Reply Text <span className="text-gray-500 text-xs">(Max 1000 characters)</span>
                  </label>
                  <textarea
                    name="poc3_poc_replied"
                    value={formData.poc3_poc_replied}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length <= 1000) {
                        handleChange(e);
                      }
                    }}
                    rows="3"
                    maxLength={1000}
                    placeholder="Enter the actual response text from POC 3 (max 1000 characters)..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                  />
                  <div className="text-xs text-gray-500 mt-1 text-right">
                    {formData.poc3_poc_replied.length}/1000 characters
                  </div>
                </div>
              </div>
              
              {/* Add POC Button after POC 3 */}
              {visiblePocCount < 4 && visiblePocCount === 3 && (
                <div className="mt-4 flex justify-center">
                  <button
                    type="button"
                    onClick={addPoc}
                    className="flex items-center gap-2 px-4 py-2 bg-[#679632] text-white rounded-lg hover:bg-[#587f2b] transition-colors font-medium"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add POC 4
                  </button>
                </div>
              )}
            </div>
            )}

            {/* POC 4 Section */}
            {visiblePocCount >= 4 && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-md font-semibold text-gray-900 mb-4">POC 4 (Optional)</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">POC 4 Name</label>
                  <input type="text" name="poc4_name" value={formData.poc4_name} onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">POC 4 Designation</label>
                  <input type="text" name="poc4_designation" value={formData.poc4_designation} onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">POC 4 Email</label>
                  <input type="email" name="poc4_email" value={formData.poc4_email} onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">POC 4 Contact No.</label>
                  <PhoneInput value={formData.poc4_contact_no}
                    onChange={(v) => setFormData(prev => ({ ...prev, poc4_contact_no: v }))} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">POC 4 LinkedIn URL</label>
                  <input
                    type="url"
                    name="poc4_linkedin_url"
                    value={formData.poc4_linkedin_url}
                    onChange={handleChange}
                    placeholder="https://www.linkedin.com/in/..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">POC 4 Response</label>
                  <select name="poc4_response_from_poc" value={formData.poc4_response_from_poc} onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent">
                    <option value="">Select...</option>
                    <option value="No Response">No Response</option>
                    <option value="Positive">Positive</option>
                    <option value="Negative">Negative</option>
                    <option value="Pending">Pending</option>
                    <option value="Interested">Interested</option>
                    <option value="Not Interested">Not Interested</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    POC 4 Reply Text <span className="text-gray-500 text-xs">(Max 1000 characters)</span>
                  </label>
                  <textarea
                    name="poc4_poc_replied"
                    value={formData.poc4_poc_replied}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length <= 1000) {
                        handleChange(e);
                      }
                    }}
                    rows="3"
                    maxLength={1000}
                    placeholder="Enter the actual response text from POC 4 (max 1000 characters)..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                  />
                  <div className="text-xs text-gray-500 mt-1 text-right">
                    {formData.poc4_poc_replied.length}/1000 characters
                  </div>
                </div>
              </div>
            </div>
            )}

            {/* Meetings live directly above the follow-up dates: both answer
                "what is next with this company", so they belong together. */}
            <LeadMeetingsSection
              leadId={lead?.id}
              companyName={formData.company_name}
              bdeName={formData.bde_name}
              pocEmails={[formData.email, formData.poc2_email, formData.poc3_email, formData.poc4_email].filter(Boolean)}
            />

            {/* Follow-up Section */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-md font-semibold text-gray-900 mb-4">Follow-up Schedule</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Follow-up 1 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Follow-up Date 1</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      name="follow_up_date_1"
                      value={formData.follow_up_date_1}
                      onChange={handleChange}
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                    />
                    {lead && lead.id && formData.follow_up_date_1 && (
                      <button
                        type="button"
                        onClick={() => handleMarkFollowUpCompleted(1)}
                        disabled={markingCompleted[1] || followUpStatuses[1]}
                        className={`flex items-center gap-1 px-3 py-2 rounded-lg text-xs whitespace-nowrap transition-colors ${
                          followUpStatuses[1] 
                            ? 'bg-gray-400 text-white cursor-not-allowed' 
                            : 'bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed'
                        }`}
                        title={followUpStatuses[1] ? "Follow-up 1 is already completed" : "Mark Follow-up 1 as completed"}
                      >
                        <Check size={14} />
                        Done
                      </button>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Follow-up 1 Update</label>
                  <input
                    type="text"
                    name="follow_up_1_update"
                    value={formData.follow_up_1_update}
                    onChange={handleChange}
                    placeholder="e.g., Called - requested proposal"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                  />
                </div>

                {/* Follow-up 2 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Follow-up Date 2</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      name="follow_up_date_2"
                      value={formData.follow_up_date_2}
                      onChange={handleChange}
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                    />
                    {lead && lead.id && formData.follow_up_date_2 && (
                      <button
                        type="button"
                        onClick={() => handleMarkFollowUpCompleted(2)}
                        disabled={markingCompleted[2] || followUpStatuses[2]}
                        className={`flex items-center gap-1 px-3 py-2 rounded-lg text-xs whitespace-nowrap transition-colors ${
                          followUpStatuses[2] 
                            ? 'bg-gray-400 text-white cursor-not-allowed' 
                            : 'bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed'
                        }`}
                        title={followUpStatuses[2] ? "Follow-up 2 is already completed" : "Mark Follow-up 2 as completed"}
                      >
                        <Check size={14} />
                        Done
                      </button>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Follow-up 2 Update</label>
                  <input
                    type="text"
                    name="follow_up_2_update"
                    value={formData.follow_up_2_update}
                    onChange={handleChange}
                    placeholder="e.g., Proposal sent - under review"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                  />
                </div>

                {/* Follow-up 3 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Follow-up Date 3</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      name="follow_up_date_3"
                      value={formData.follow_up_date_3}
                      onChange={handleChange}
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                    />
                    {lead && lead.id && formData.follow_up_date_3 && (
                      <button
                        type="button"
                        onClick={() => handleMarkFollowUpCompleted(3)}
                        disabled={markingCompleted[3] || followUpStatuses[3]}
                        className={`flex items-center gap-1 px-3 py-2 rounded-lg text-xs whitespace-nowrap transition-colors ${
                          followUpStatuses[3] 
                            ? 'bg-gray-400 text-white cursor-not-allowed' 
                            : 'bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed'
                        }`}
                        title={followUpStatuses[3] ? "Follow-up 3 is already completed" : "Mark Follow-up 3 as completed"}
                      >
                        <Check size={14} />
                        Done
                      </button>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Follow-up 3 Update</label>
                  <input
                    type="text"
                    name="follow_up_3_update"
                    value={formData.follow_up_3_update}
                    onChange={handleChange}
                    placeholder="e.g., Deal closed - contract signed"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                  />
                </div>

                {/* Follow-up Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Follow-up Status</label>
                  <select
                    name="follow_up_status"
                    value={formData.follow_up_status}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                  >
                    <option value="Upcoming">Upcoming</option>
                    <option value="Pending">Pending</option>
                    <option value="Active">Active</option>
                    <option value="Completed">Completed</option>
                  </select>
                </div>

                {/* Clear & Archive Follow-ups Button - Shows when all 3 are used */}
                <div className="flex items-end">
                  {allFollowUpsUsed && (
                    <button
                      type="button"
                      onClick={handleRotateFollowUps}
                      className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors"
                      title="Save all follow-up notes to Company Overview and clear all slots for new scheduling"
                    >
                      🔄 Clear & Archive (Free all slots)
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Timestamp Information - Only show for existing leads */}
            {lead && formData.created_at && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Created At</label>
                  <div className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                    {new Date(formData.created_at).toLocaleString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                      hour12: true
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Post Sales Section - Only show for existing leads */}
            {lead && lead.id && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Post Sales Information</h3>
                  {!loadingPostSales && !editingPostSales && (
                    <button
                      type="button"
                      onClick={() => setEditingPostSales(true)}
                      className="text-sm text-[#679632] hover:text-[#587f2b] font-medium"
                    >
                      {postSalesData ? 'Edit' : 'Add post-sales info'}
                    </button>
                  )}
                </div>
                {loadingPostSales ? (
                  <div className="text-center py-4">
                    <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-[#679632]"></div>
                    <p className="mt-2 text-sm text-gray-500">Loading post sales data...</p>
                  </div>
                ) : editingPostSales ? (
                  /* Inline editable form — pre-filled from postSalesData if any.
                     Saves a partial post_sales record via createOrUpdatePostSales.
                     Follow-ups 2-4 remain owned by the dedicated Post-Sales tab. */
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Stage</label>
                        <select
                          value={postSalesForm.stage}
                          onChange={(e) => setPostSalesForm(f => ({ ...f, stage: e.target.value }))}
                          className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                        >
                          <option value="followup">followup</option>
                          <option value="in_progress">in_progress</option>
                          <option value="pending">pending</option>
                          <option value="done">done</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Call Status</label>
                        <select
                          value={postSalesForm.call_status}
                          onChange={(e) => setPostSalesForm(f => ({ ...f, call_status: e.target.value }))}
                          className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                        >
                          <option value="">— select —</option>
                          <option value="answered">answered</option>
                          <option value="not answered">not answered</option>
                          <option value="busy">busy</option>
                          <option value="voicemail">voicemail</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Contact Number</label>
                        <input
                          type="text"
                          value={postSalesForm.contact_number}
                          onChange={(e) => setPostSalesForm(f => ({ ...f, contact_number: e.target.value }))}
                          placeholder="+1 555 ..."
                          className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Time Zone</label>
                        <input
                          type="text"
                          value={postSalesForm.time_zone}
                          onChange={(e) => setPostSalesForm(f => ({ ...f, time_zone: e.target.value }))}
                          placeholder="America/Chicago"
                          className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Email (post-sales)</label>
                        <input
                          type="email"
                          value={postSalesForm.email}
                          onChange={(e) => setPostSalesForm(f => ({ ...f, email: e.target.value }))}
                          className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Email Type</label>
                        <select
                          value={postSalesForm.email_type}
                          onChange={(e) => setPostSalesForm(f => ({ ...f, email_type: e.target.value }))}
                          className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                        >
                          <option value="">— select —</option>
                          <option value="personal">personal</option>
                          <option value="provided">provided</option>
                          <option value="found">found</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">First follow-up date</label>
                        <input
                          type="date"
                          value={postSalesForm.follow_up_1_date}
                          onChange={(e) => setPostSalesForm(f => ({ ...f, follow_up_1_date: e.target.value }))}
                          className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">First follow-up note</label>
                        <input
                          type="text"
                          value={postSalesForm.follow_up_1_note}
                          onChange={(e) => setPostSalesForm(f => ({ ...f, follow_up_1_note: e.target.value }))}
                          className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                      <textarea
                        rows={3}
                        value={postSalesForm.notes}
                        onChange={(e) => setPostSalesForm(f => ({ ...f, notes: e.target.value }))}
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                      />
                    </div>
                    <div className="flex gap-2 pt-1">
                      <button
                        type="button"
                        onClick={handleSavePostSales}
                        disabled={savingPostSales}
                        className="px-4 py-2 bg-[#679632] text-white text-sm rounded hover:bg-[#587f2b] disabled:opacity-50"
                      >
                        {savingPostSales ? 'Saving…' : 'Save post-sales'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingPostSales(false)}
                        disabled={savingPostSales}
                        className="px-3 py-2 border border-gray-300 text-sm rounded hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <p className="text-[11px] text-gray-500 ml-1 self-center">Follow-ups 2–4 still live in the Post-Sales tab.</p>
                    </div>
                  </div>
                ) : postSalesData ? (
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Post Sales Handler</label>
                        <div className="text-sm bg-white border border-gray-200 rounded px-3 py-2">
                          {postSalesData.post_sales_bde_name ? (
                            <span className="font-semibold text-purple-700">{postSalesData.post_sales_bde_name}</span>
                          ) : (
                            <span className="text-gray-400 italic">Not assigned</span>
                          )}
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Email (post-sales)</label>
                        <div className="text-sm bg-white border border-gray-200 rounded px-3 py-2 break-all">
                          {postSalesData.email ? (
                            <span className="text-purple-700">{postSalesData.email}</span>
                          ) : (
                            <span className="text-gray-400">Not logged</span>
                          )}
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Contact Number</label>
                        <div className="text-sm text-gray-900 bg-white border border-gray-200 rounded px-3 py-2">
                          {postSalesData.contact_number || 'Not logged'}
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Time Zone</label>
                        <div className="text-sm text-gray-900 bg-white border border-gray-200 rounded px-3 py-2">
                          {postSalesData.time_zone || 'Not logged'}
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Call Status</label>
                        <div className="text-sm text-gray-900 bg-white border border-gray-200 rounded px-3 py-2">
                          {postSalesData.call_status ? (
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              postSalesData.call_status === 'answered' ? 'bg-green-100 text-green-800' :
                              postSalesData.call_status === 'not answered' ? 'bg-red-100 text-red-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {postSalesData.call_status}
                            </span>
                          ) : 'Not logged'}
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Email Type</label>
                        <div className="text-sm text-gray-900 bg-white border border-gray-200 rounded px-3 py-2">
                          {postSalesData.email_type ? (
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              postSalesData.email_type === 'personal' ? 'bg-blue-100 text-blue-800' :
                              postSalesData.email_type === 'provided' ? 'bg-green-100 text-green-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {postSalesData.email_type}
                            </span>
                          ) : 'Not logged'}
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Stage</label>
                        <div className="text-sm text-gray-900 bg-white border border-gray-200 rounded px-3 py-2">
                          {postSalesData.stage ? (
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              postSalesData.stage === 'done' ? 'bg-green-100 text-green-800' :
                              postSalesData.stage === 'followup' ? 'bg-yellow-100 text-yellow-800' :
                              postSalesData.stage === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {postSalesData.stage}
                            </span>
                          ) : 'Not set'}
                        </div>
                      </div>
                      {postSalesData.updated_at && (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Last Updated</label>
                          <div className="text-sm text-gray-900 bg-white border border-gray-200 rounded px-3 py-2">
                            {new Date(postSalesData.updated_at).toLocaleString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: true
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                    {postSalesData.notes && (
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
                        <div className="text-sm text-gray-900 bg-white border border-gray-200 rounded px-3 py-2 whitespace-pre-wrap">
                          {postSalesData.notes}
                        </div>
                      </div>
                    )}
                    <div className="text-xs text-gray-500 italic mt-2">
                      Click <strong>Edit</strong> above to update post-sales fields without leaving this tab.
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <p className="text-sm text-gray-500">No post-sales information logged yet.</p>
                    <p className="text-xs text-gray-400 mt-1">Click <strong>Add post-sales info</strong> above to log it from here.</p>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3 mt-6">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-[#679632] text-white py-2 px-4 rounded-lg hover:bg-[#587f2b] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Saving...' : (lead ? 'Update Lead' : 'Create Lead')}
              </button>
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LeadModal;
